// src/pages/FinalResultSummary.jsx
import React, { useEffect, useRef, useState, useMemo } from "react";
import api from "../api";
import Swal from "sweetalert2";
import { Modal, Button, ProgressBar } from "react-bootstrap";

/* ============================================================
 * ✅ CONFIG
 * ============================================================ */
const PDF_ENDPOINT = "/report-card/generate-pdf/report-card";

/* ============================================================
 * ✅ Student photo helpers
 * Handles all cases:
 * 1) photo = only filename
 * 2) photo = /uploads/... path
 * 3) photo = full https URL
 * 4) photo_url accidentally built as /uploads/photoes/students/https%3A...
 * ============================================================ */
const normalizeBaseURL = (value) => {
  const raw = String(value || "").trim().replace(/\\/g, "/");
  if (!raw) return window.location.origin;
  if (/^https?:\/\//i.test(raw)) return raw.replace(/\/+$/, "");
  if (raw.startsWith("//")) return `${window.location.protocol}${raw}`.replace(/\/+$/, "");
  if (raw.startsWith("/")) return `${window.location.origin}${raw}`.replace(/\/+$/, "");
  return `${window.location.origin}/${raw}`.replace(/\/+$/, "");
};

const envApiBase = import.meta.env?.VITE_API_BASE_URL || import.meta.env?.VITE_API_URL || "";
const apiBase = normalizeBaseURL(api?.defaults?.baseURL || envApiBase || window.location.origin);

// Static uploads are served from API host root, not from /api.
const assetBase = apiBase.replace(/\/api$/i, "");

const safeDecodeURIComponent = (value) => {
  try {
    return decodeURIComponent(String(value || ""));
  } catch {
    return String(value || "");
  }
};

const encodeAssetSegment = (segment) => {
  try {
    return encodeURIComponent(decodeURIComponent(segment));
  } catch {
    return encodeURIComponent(segment);
  }
};

const encodeAssetPath = (path) =>
  String(path || "")
    .split("/")
    .filter(Boolean)
    .map(encodeAssetSegment)
    .join("/");

const isEmptyPhotoValue = (value) => {
  const s = String(value || "").trim();
  return !s || s === "-" || ["null", "undefined", "none", "na", "n/a"].includes(s.toLowerCase());
};

// If backend receives photo as a full URL but still wraps it like:
// https://api.../uploads/photoes/students/https%3A%2F%2Fapi...%2Fuploads%2Fphotoes%2Fstudents%2Ffile.jpeg
// this function extracts the real URL back.
const unwrapWrappedStudentPhotoURL = (value) => {
  const raw = String(value || "").trim().replace(/\\/g, "/");
  if (!raw) return "";

  const patterns = [
    /\/uploads\/photoes\/students\/(.+)$/i,
    /\/uploads\/photos\/students\/(.+)$/i,
    /\/photoes\/students\/(.+)$/i,
    /\/photos\/students\/(.+)$/i,
  ];

  for (const p of patterns) {
    const match = raw.match(p);
    if (!match?.[1]) continue;

    const decoded = safeDecodeURIComponent(match[1]).trim();
    if (/^https?:\/\//i.test(decoded)) return decoded;

    // Sometimes only encoded upload path is inside last segment.
    if (decoded.startsWith("/uploads/") || decoded.startsWith("uploads/")) {
      return buildStudentPhotoURL(decoded);
    }
  }

  return raw;
};

const buildStudentPhotoURL = (value) => {
  if (isEmptyPhotoValue(value)) return "";

  const unwrapped = unwrapWrappedStudentPhotoURL(value);
  const raw = String(unwrapped || "").trim().replace(/\\/g, "/");
  if (isEmptyPhotoValue(raw)) return "";

  if (/^(data:|blob:|https?:\/\/)/i.test(raw)) return raw;
  if (raw.startsWith("//")) return `${window.location.protocol}${raw}`;

  const clean = raw.replace(/^\/+/, "");

  if (clean.startsWith("uploads/")) return `${assetBase}/${encodeAssetPath(clean)}`;
  if (clean.startsWith("photoes/") || clean.startsWith("photos/")) {
    return `${assetBase}/uploads/${encodeAssetPath(clean)}`;
  }

  // If API sends path like photoes/students/file.jpeg or students/file.jpeg.
  if (clean.includes("/")) return `${assetBase}/${encodeAssetPath(clean)}`;

  // Only filename saved in DB.
  return `${assetBase}/uploads/photoes/students/${encodeAssetSegment(clean)}`;
};


const buildPublicAssetURL = (value) => {
  if (isEmptyPhotoValue(value)) return "";

  const raw = String(value || "").trim().replace(/\\/g, "/");
  if (isEmptyPhotoValue(raw)) return "";

  if (/^(data:|blob:|https?:\/\/)/i.test(raw)) return raw;
  if (raw.startsWith("//")) return `${window.location.protocol}${raw}`;

  const clean = raw.replace(/^\/+/, "");
  if (clean.startsWith("uploads/")) return `${assetBase}/${encodeAssetPath(clean)}`;

  return `${assetBase}/${encodeAssetPath(clean)}`;
};

const getStudentPhotoCandidates = (info = {}) => [
  info?.photo,
  info?.Photo,
  info?.student_photo,
  info?.studentPhoto,
  info?.Student_Photo,
  info?.profile_photo,
  info?.profilePhoto,
  info?.image,
  info?.photo_path,
  info?.photoPath,
  info?.photo_url,
  info?.photoUrl,
  info?.student_photo_url,
  info?.studentPhotoUrl,
].filter((x) => !isEmptyPhotoValue(x));

const NO_PHOTO_SVG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="140">
       <defs>
         <linearGradient id="g" x1="0" x2="1">
           <stop offset="0" stop-color="#e0f2fe"/>
           <stop offset="1" stop-color="#eef2ff"/>
         </linearGradient>
       </defs>
       <rect width="100%" height="100%" fill="url(#g)"/>
       <circle cx="60" cy="48" r="24" fill="#cbd5e1"/>
       <rect x="20" y="82" width="80" height="26" rx="12" fill="#cbd5e1"/>
     </svg>`
  );

const getStudentPhotoURL = (info = {}) => {
  const candidates = getStudentPhotoCandidates(info);

  for (const candidate of candidates) {
    const url = buildStudentPhotoURL(candidate);
    if (url) return url;
  }

  return NO_PHOTO_SVG;
};

const blobToDataURL = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result || "");
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const imageUrlToDataURL = async (url) => {
  if (!url || url === NO_PHOTO_SVG || /^data:/i.test(url)) return url;

  try {
    const res = await fetch(url, {
      method: "GET",
      mode: "cors",
      credentials: "omit",
      cache: "no-store",
    });

    if (!res.ok) throw new Error(`Image fetch failed: ${res.status}`);

    const blob = await res.blob();
    if (!blob?.type?.startsWith("image/")) throw new Error("Fetched file is not an image");

    return await blobToDataURL(blob);
  } catch (error) {
    // Keep the original URL as fallback. If server-side PDF browser can access it,
    // it will still print. For guaranteed data URLs, ensure /uploads has CORS headers.
    console.warn("Student photo could not be embedded as data URL:", url, error?.message || error);
    return url;
  }
};

/* ============================================================
 * ✅ Date helpers
 * ============================================================ */
const pad2 = (n) => String(n).padStart(2, "0");

const formatDOB = (raw) => {
  if (!raw) return "-";
  const s = String(raw).trim();
  if (!s) return "-";

  if (/^\d{2}-\d{2}-\d{4}$/.test(s)) return s;

  const m1 = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m1) return `${pad2(m1[1])}-${pad2(m1[2])}-${m1[3]}`;

  const m2 = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m2) return `${pad2(m2[3])}-${pad2(m2[2])}-${m2[1]}`;

  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    return `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()}`;
  }

  return s;
};

const formatDisplayDate = (raw) => {
  if (!raw) return "-";
  const s = String(raw).trim();
  if (!s) return "-";

  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;

  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    return `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()}`;
  }

  return s;
};


const hasVisualReportCardLayout = (template) => {
  if (!template) return false;
  let layout = template.layout_json;
  if (typeof layout === "string") {
    try { layout = JSON.parse(layout); } catch (_) { layout = null; }
  }
  const elements = Array.isArray(layout) ? layout : layout?.elements;
  return Boolean(Array.isArray(elements) && elements.length);
};

/* ============================================================
 * ✅ Smart report-card template helpers
 * Used by the reusable PDF/image background template renderer.
 * ============================================================ */
const smartTemplateSlug = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || "field";

const smartTemplateAliases = (value) => {
  const base = smartTemplateSlug(value);
  const compact = base.replace(/_/g, "");
  return [...new Set([base, compact].filter(Boolean))];
};



/* ============================================================
 * ✅ Attendance helpers
 * ============================================================ */
const safeInt = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const buildPresentTotalText = (att) => {
  const p = safeInt(att?.present_days);
  const t = safeInt(att?.total_days);
  if (p == null && t == null) return "-";
  return `${p ?? 0} / ${t ?? 0}`;
};

const buildAttendancePercent = (att) => {
  const p = safeInt(att?.present_days);
  const t = safeInt(att?.total_days);
  if (p == null || t == null || t <= 0) return null;
  return Number(((p / t) * 100).toFixed(2));
};

const getPrimaryHealthInfo = (info = {}) => {
  const present = info?.health_present_days ?? info?.present_days ?? null;
  const working = info?.health_working_days ?? info?.working_days ?? null;

  return {
    height: info?.height || "-",
    weight: info?.weight || "-",
    dental: info?.dental_checkup || info?.dental || "-",
    vision: info?.vision || "-",
    blood_group: info?.blood_group_snapshot || info?.blood_group || info?.b_group || "-",
    assessment_date: info?.assessment_date || "",
    present_days: present !== null && present !== undefined && present !== "" ? present : "-",
    working_days: working !== null && working !== undefined && working !== "" ? working : "-",
  };
};

/* ============================================================
 * ✅ MARKS / GRADE helpers
 * ============================================================ */
const isNumeric = (v) => v != null && v !== "" && Number.isFinite(Number(v));

const sumMarksOnly = (arr = []) =>
  (arr || []).reduce((a, x) => a + (isNumeric(x?.marks) ? Number(x.marks) : 0), 0);

const hasAnyMarks = (arr = []) => (arr || []).some((x) => isNumeric(x?.marks));

const pickGrade = (arr = []) => {
  const hit = (arr || []).find((x) => x?.grade != null && String(x.grade).trim() !== "");
  return hit?.grade || "-";
};

const sumWeightedOnly = (arr = []) =>
  (arr || []).reduce(
    (a, x) => a + (isNumeric(x?.weighted_marks) ? Number(x.weighted_marks) : 0),
    0
  );

const sumMaxWeight = (arr = []) =>
  (arr || []).reduce(
    (a, x) => a + (isNumeric(x?.weightage_percent) ? Number(x.weightage_percent) : 0),
    0
  );

const hasDisplayRank = (rank) => {
  if (rank == null) return false;
  const s = String(rank).trim();
  return s !== "" && s !== "-" && s.toLowerCase() !== "null" && s.toLowerCase() !== "undefined";
};

/* ============================================================
 * ✅ Grade from schema
 * ============================================================ */
const gradeFromSchema = (percent, gradeSchema = []) => {
  if (percent == null || !Number.isFinite(Number(percent))) return "-";
  const p = Number(percent);
  for (const g of gradeSchema || []) {
    const min = Number(g?.min_percent);
    const max = Number(g?.max_percent);
    if (Number.isFinite(min) && Number.isFinite(max) && p >= min && p <= max) {
      return g?.grade ?? "-";
    }
  }
  return "-";
};

/* ============================================================
 * ✅ Header HTML cleanup
 * ============================================================ */
const sanitizeHeaderHtml = (raw) => {
  if (!raw) return "";

  let html = String(raw);

  html = html.replace(/<[^>]*>\s*Excellence\s*\/\s*Discipline\s*<\/[^>]*>/gi, "");
  html = html.replace(/Excellence\s*\/\s*Discipline/gi, "");

  html = html.replace(
    /(ACADEMIC SESSION[^<]*)(\s*)(Annual Report Card)/gi,
    `<div style="display:block;">$1</div><div style="display:block;">$3</div>`
  );

  html = html.replace(
    /(Academic Session[^<]*)(\s*)(Annual Report Card)/gi,
    `<div style="display:block;">$1</div><div style="display:block;">$3</div>`
  );

  return html;
};

/* ============================================================
 * ✅ Subject helpers
 * ============================================================ */
const isDrawingSubject = (name = "") => {
  const s = String(name || "").trim().toLowerCase();
  return ["drawing", "art", "arts", "drawing / art", "art & craft", "craft"].includes(s);
};

const normalizeSubjectType = (value) =>
  String(value || "Scholastic").trim() === "Co-Scholastic"
    ? "Co-Scholastic"
    : "Scholastic";

const isCoScholasticComponent = (component = {}) =>
  normalizeSubjectType(component?.subject_type ?? component?.subjectType) === "Co-Scholastic";

const getNonDrawingSubjects = (student) =>
  Array.from(
    new Set(
      (student?.components || [])
        .filter((component) => !isCoScholasticComponent(component))
        .map((component) => component.subject_name)
        .filter(Boolean)
    )
  ).filter((name) => !isDrawingSubject(name));

const getCoScholasticSubjectNames = (student) =>
  Array.from(
    new Set(
      (student?.components || [])
        .filter(isCoScholasticComponent)
        .map((component) => component.subject_name)
        .filter(Boolean)
    )
  );

const getDrawingGradeForTerm = (student, termId, exams, gradeSchema) => {
  if (!termId) return "-";

  const items = (student?.components || []).filter((c) => {
    const exTerm = exams.find((e) => e.id === c.exam_id)?.term_id;
    return isDrawingSubject(c.subject_name) && Number(exTerm) === Number(termId);
  });

  if (!items.length) return "-";

  const directGrade = pickGrade(items);
  if (directGrade && directGrade !== "-") return directGrade;

  const wTotal = sumWeightedOnly(items);
  const wMax = sumMaxWeight(items);
  const percent = wMax > 0 ? (wTotal / wMax) * 100 : null;

  return percent != null ? gradeFromSchema(percent, gradeSchema) : "-";
};

const buildGradeRangeFooterText = (gradeSchema = []) => {
  if (!gradeSchema?.length) return "";

  return (gradeSchema || [])
    .map((g) => {
      const min = g?.min_percent;
      const max = g?.max_percent;
      const grade = g?.grade;
      if (min == null || max == null || !grade) return "";
      return `${min}-${max} = ${grade}`;
    })
    .filter(Boolean)
    .join(", ");
};

const FinalResultSummary = () => {
  const [sessions, setSessions] = useState([]);
  const [classList, setClassList] = useState([]);
  const [sections, setSections] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [exams, setExams] = useState([]);
  const [showTotals, setShowTotals] = useState(true);

  const [studentInfoMap, setStudentInfoMap] = useState({});
  const [coScholasticByTerm, setCoScholasticByTerm] = useState({});
  const [remarksByTerm, setRemarksByTerm] = useState({});
  const [attendanceByTerm, setAttendanceByTerm] = useState({});
  const [promotionDecisionByTerm, setPromotionDecisionByTerm] = useState({});
  const [gradeSchema, setGradeSchema] = useState([]);

  const [filters, setFilters] = useState({
    session_id: "",
    class_id: "",
    section_id: "",
    exam_ids: [],
    subjectComponents: [{ subject_id: "", selected_components: {}, availableComponents: [] }],
  });

  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportFormat, setReportFormatState] = useState(null);
  const reportFormatRef = useRef(null);

  const setReportFormat = (format) => {
    reportFormatRef.current = format;
    setReportFormatState(format);
  };

  // ✅ Backend report-card template selection
  const [reportTemplates, setReportTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [reportFormats, setReportFormats] = useState([]);
  const [selectedReportFormatId, setSelectedReportFormatId] = useState("");
  const [loadingReportFormats, setLoadingReportFormats] = useState(false);

  const [numberFormat] = useState({
    decimalPoints: 2,
    rounding: "none",
  });

  const [pdfProgressVisible, setPdfProgressVisible] = useState(false);
  const [pdfPercent, setPdfPercent] = useState(0);
  const [pdfMessage, setPdfMessage] = useState("Preparing…");
  const abortGenRef = useRef(null);

  const [pdfMode, setPdfMode] = useState("all");
  const [pdfSingleId, setPdfSingleId] = useState("");
  const [pdfFrom, setPdfFrom] = useState("");
  const [pdfTo, setPdfTo] = useState("");

  const selectedReportTemplate = useMemo(() => {
    return (reportTemplates || []).find((t) => String(t.id) === String(selectedTemplateId)) || null;
  }, [reportTemplates, selectedTemplateId]);

  const isSmartReportTemplate = useMemo(() => {
    if (!selectedReportTemplate) return false;
    return Boolean(selectedReportTemplate.is_smart_template || hasVisualReportCardLayout(selectedReportTemplate));
  }, [selectedReportTemplate]);

  const selectedReportFormat = useMemo(() => {
    return (reportFormats || []).find((f) => String(f.id) === String(selectedReportFormatId)) || null;
  }, [reportFormats, selectedReportFormatId]);

  // ✅ Header/footer/logo priority: class-wise ReportCardFormat first, then template fallback.
  // This makes the format configured from ReportCardFormats print at the top of the report card.
  const getReportCardFormatValue = (field) => {
    const activeReportFormat = reportFormatRef.current || reportFormat;
    const classFormatValue = activeReportFormat?.[field];
    if (classFormatValue !== undefined && classFormatValue !== null && String(classFormatValue).trim() !== "") {
      return classFormatValue;
    }

    const templateValue = selectedReportTemplate?.[field];
    if (templateValue !== undefined && templateValue !== null && String(templateValue).trim() !== "") {
      return templateValue;
    }

    return "";
  };

  const getReportCardHeaderHtml = () => getReportCardFormatValue("header_html");
  const getReportCardFooterHtml = () => getReportCardFormatValue("footer_html");

  const getReportCardSchoolLogoUrl = (formatAssets = {}) =>
    formatAssets.school_logo_url || buildPublicAssetURL(getReportCardFormatValue("school_logo_url"));

  const getReportCardBoardLogoUrl = (formatAssets = {}) =>
    formatAssets.board_logo_url || buildPublicAssetURL(getReportCardFormatValue("board_logo_url"));

  const prepareReportFormatAssetsForPdf = async () => {
    const schoolLogoUrl = buildPublicAssetURL(getReportCardFormatValue("school_logo_url"));
    const boardLogoUrl = buildPublicAssetURL(getReportCardFormatValue("board_logo_url"));

    const [schoolLogoDataUrl, boardLogoDataUrl] = await Promise.all([
      schoolLogoUrl ? imageUrlToDataURL(schoolLogoUrl) : Promise.resolve(""),
      boardLogoUrl ? imageUrlToDataURL(boardLogoUrl) : Promise.resolve(""),
    ]);

    return {
      school_logo_url: schoolLogoDataUrl || schoolLogoUrl || "",
      board_logo_url: boardLogoDataUrl || boardLogoUrl || "",
    };
  };

  const normalizeReportFormatResponse = (payload) => {
    const candidate =
      payload?.format ||
      payload?.reportFormat ||
      payload?.report_card_format ||
      payload?.data?.format ||
      payload?.data?.reportFormat ||
      payload?.data?.report_card_format ||
      payload?.data ||
      payload;

    const item = Array.isArray(candidate) ? candidate[0] : candidate;

    if (
      item &&
      typeof item === "object" &&
      (item.header_html ||
        item.footer_html ||
        item.school_logo_url ||
        item.board_logo_url ||
        item.title)
    ) {
      return item;
    }

    return null;
  };

  const pickDefaultReportFormat = (formats = [], class_id) => {
    const classId = Number(class_id);
    const preferredOrientation = Number.isFinite(classId) && classId <= 8 ? "landscape" : "portrait";
    return (
      formats.find((f) => String(f.orientation || "").toLowerCase() === preferredOrientation) ||
      formats[0] ||
      null
    );
  };

  const loadReportCardFormats = async (class_id) => {
    try {
      setLoadingReportFormats(true);
      const res = await api.get("/report-card-formats");
      const list = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.formats)
        ? res.data.formats
        : [];
      setReportFormats(list);

      const defaultFormat = pickDefaultReportFormat(list, class_id);
      setSelectedReportFormatId(defaultFormat?.id ? String(defaultFormat.id) : "");
      setReportFormat(defaultFormat || null);
      return defaultFormat;
    } catch (error) {
      console.error("Failed to load report card formats:", error);
      setReportFormats([]);
      setSelectedReportFormatId("");
      setReportFormat(null);
      Swal.fire("Error", "Failed to load report card formats", "error");
      return null;
    } finally {
      setLoadingReportFormats(false);
    }
  };

  const handleReportFormatChange = (formatId) => {
    setSelectedReportFormatId(formatId);
    const selected = (reportFormats || []).find((f) => String(f.id) === String(formatId)) || null;
    setReportFormat(selected);
  };

  const loadReportFormatForClass = async (class_id, session_id) => {
    if (!class_id) {
      setReportFormat(null);
      return null;
    }

    try {
      const params = { class_id: Number(class_id) };
      if (session_id) params.session_id = Number(session_id);

      const res = await api.get("/report-card-formats/format-by-class", { params });
      const format = normalizeReportFormatResponse(res.data);

      console.log("✅ FORMAT-BY-CLASS RAW RESPONSE:", res.data);
      console.log("✅ NORMALIZED REPORT FORMAT:", format);

      setReportFormat(format);
      return format;
    } catch (error) {
      console.warn("Report card format not found. Using selected template instead:", error);
      setReportFormat(null);
      return null;
    }
  };

  const normalizeStudentListPayload = (payload) => {
    const candidate =
      payload?.students ||
      payload?.data?.students ||
      payload?.data ||
      payload;

    if (Array.isArray(candidate)) return candidate;
    if (candidate && typeof candidate === "object") return [candidate];
    return [];
  };

  const buildStudentFallbackLookup = (rows = []) => {
    const byId = {};
    const byAdmission = {};

    (rows || []).forEach((row) => {
      if (!row || typeof row !== "object") return;

      const id = row.id ?? row.student_id ?? row.studentId;
      if (id !== undefined && id !== null) byId[String(id)] = row;

      const admission = row.admission_number ?? row.AdmissionNumber ?? row.admissionNo;
      if (admission !== undefined && admission !== null && String(admission).trim() !== "") {
        byAdmission[String(admission).trim()] = row;
      }
    });

    return { byId, byAdmission };
  };

  const fetchStudentFallbackLookupForClass = async ({ session_id, class_id, section_id }) => {
    const attempts = [
      {
        url: "/students/searchByClassAndSection",
        params: {
          class_id: Number(class_id),
          section_id: Number(section_id),
          session_id: session_id ? Number(session_id) : undefined,
        },
      },
      {
        url: "/students/by-session",
        params: {
          class_id: Number(class_id),
          section_id: Number(section_id),
          session_id: session_id ? Number(session_id) : undefined,
        },
      },
    ];

    for (const attempt of attempts) {
      try {
        const res = await api.get(attempt.url, { params: attempt.params });
        const rows = normalizeStudentListPayload(res.data);
        if (rows.length) {
          console.log(`✅ Student photo fallback loaded from ${attempt.url}:`, rows[0]);
          return buildStudentFallbackLookup(rows);
        }
      } catch (error) {
        console.warn(`Student fallback API failed: ${attempt.url}`, error?.response?.status || error?.message || error);
      }
    }

    return { byId: {}, byAdmission: {} };
  };

  const fetchStudentInfoByAdmissionNumber = async (admissionNumber) => {
    const admission = String(admissionNumber || "").trim();
    if (!admission) return null;

    try {
      const res = await api.get(`/students/admission/${encodeURIComponent(admission)}`);
      const rows = normalizeStudentListPayload(res.data);
      return rows[0] || null;
    } catch (error) {
      console.warn("Student photo admission fallback failed:", admission, error?.response?.status || error?.message || error);
      return null;
    }
  };

  const hasStudentPhotoCandidate = (info = {}) => getStudentPhotoCandidates(info).length > 0;

  const mergeStudentInfoWithFallback = (student = {}, reportInfo = {}, fallbackLookup = null) => {
    const idKey = String(student?.id ?? reportInfo?.id ?? reportInfo?.student_id ?? "");
    const admission = String(
      reportInfo?.admission_number ||
        reportInfo?.AdmissionNumber ||
        student?.admission_number ||
        student?.AdmissionNumber ||
        ""
    ).trim();

    const fallback =
      fallbackLookup?.byId?.[idKey] ||
      (admission ? fallbackLookup?.byAdmission?.[admission] : null) ||
      {};

    // fallback first, then report-card info, so report-card fields keep priority,
    // but photo/photo_url from fallback fills missing values.
    return {
      ...fallback,
      ...reportInfo,
      photo: reportInfo?.photo || reportInfo?.Photo || fallback?.photo || fallback?.Photo || null,
      photo_url:
        reportInfo?.photo_url ||
        reportInfo?.photoUrl ||
        reportInfo?.student_photo_url ||
        fallback?.photo_url ||
        fallback?.photoUrl ||
        fallback?.student_photo_url ||
        null,
      photoUrl:
        reportInfo?.photoUrl ||
        reportInfo?.photo_url ||
        fallback?.photoUrl ||
        fallback?.photo_url ||
        null,
    };
  };

  const prepareStudentPhotoDataUrlsForPdf = async (studentsForPdf = []) => {
    let fallbackLookup = { byId: {}, byAdmission: {} };

    try {
      fallbackLookup = await fetchStudentFallbackLookupForClass({
        session_id: filters.session_id,
        class_id: filters.class_id,
        section_id: filters.section_id,
      });
    } catch {
      fallbackLookup = { byId: {}, byAdmission: {} };
    }

    const nextInfoMap = { ...(studentInfoMap || {}) };

    await Promise.all(
      (studentsForPdf || []).map(async (student) => {
        const currentInfo = nextInfoMap[student.id] || {};
        let mergedInfo = mergeStudentInfoWithFallback(student, currentInfo, fallbackLookup);

        if (!hasStudentPhotoCandidate(mergedInfo)) {
          const admission = mergedInfo?.admission_number || student?.admission_number;
          const admissionInfo = await fetchStudentInfoByAdmissionNumber(admission);
          if (admissionInfo) mergedInfo = mergeStudentInfoWithFallback(student, mergedInfo, buildStudentFallbackLookup([admissionInfo]));
        }

        const photoUrl = getStudentPhotoURL(mergedInfo);
        const embeddedPhotoSrc = await imageUrlToDataURL(photoUrl);

        nextInfoMap[student.id] = {
          ...currentInfo,
          ...mergedInfo,
          __pdfPhotoSrc: embeddedPhotoSrc || photoUrl || NO_PHOTO_SVG,
        };

        console.log("✅ REPORT CARD PHOTO DEBUG:", {
          student_id: student.id,
          admission_number: mergedInfo?.admission_number,
          photo: mergedInfo?.photo,
          photo_url: mergedInfo?.photo_url || mergedInfo?.photoUrl,
          final_src_preview: String(nextInfoMap[student.id].__pdfPhotoSrc || "").slice(0, 120),
        });
      })
    );

    return nextInfoMap;
  };

  useEffect(() => {
    loadSessions();
    loadClasses();
    loadSections();
    loadGradeSchema();
  }, []);

  useEffect(() => {
    loadExams(filters.session_id || undefined);
  }, [filters.session_id]);

  const loadSessions = async () => {
    try {
      const res = await api.get("/sessions");
      const list = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.sessions)
        ? res.data.sessions
        : [];

      setSessions(list);

      const active = list.find((x) => x?.is_active);
      if (active?.id) {
        setFilters((prev) => ({
          ...prev,
          session_id: prev.session_id || String(active.id),
        }));
      }
    } catch {
      Swal.fire("Error", "Failed to load sessions", "error");
    }
  };

  const loadClasses = async () => {
    try {
      const res = await api.get("/classes");
      setClassList(res.data || []);
    } catch {
      Swal.fire("Error", "Failed to load classes", "error");
    }
  };

  const loadSections = async () => {
    try {
      const res = await api.get("/sections");
      setSections(res.data || []);
    } catch {
      Swal.fire("Error", "Failed to load sections", "error");
    }
  };

  const loadExams = async (sessionId) => {
    try {
      const params = {};
      if (sessionId) params.session_id = Number(sessionId);
      const res = await api.get("/exams", { params });
      setExams(res.data || []);
    } catch {
      Swal.fire("Error", "Failed to load exams", "error");
    }
  };

  const loadGradeSchema = async () => {
    try {
      const res = await api.get("/grade-schemes");
      setGradeSchema(res.data.data || []);
    } catch {
      Swal.fire("Error", "Failed to load grade schema", "error");
    }
  };

  const handleExamChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions).map((opt) =>
      parseInt(opt.value, 10)
    );
    setFilters((prev) => ({ ...prev, exam_ids: selectedOptions }));
  };

  const loadSubjects = async (class_id, session_id) => {
    try {
      const params = { class_id };
      if (session_id) params.session_id = Number(session_id);
      const res = await api.get("/subjects", { params });
      const list = Array.isArray(res.data.subjects) ? res.data.subjects : [];
      setSubjects(list);
      return list;
    } catch {
      Swal.fire("Error", "Failed to load subjects", "error");
      setSubjects([]);
      return [];
    }
  };

  const loadReportCardTemplates = async (class_id, session_id) => {
    try {
      setLoadingTemplates(true);

      const res = await api.get("/report-card/templates", {
        params: {
          class_id: Number(class_id),
          session_id: session_id ? Number(session_id) : undefined,
        },
      });

      const list = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.templates)
        ? res.data.templates
        : [];

      setReportTemplates(list);

      // ✅ For PLAY GROUP to UKG, prefer class-specific Primary Section template.
      // Otherwise the global Default Report Card can get selected first.
      const isPrimaryClass = ["0", "1", "2", "3"].includes(String(class_id));

      const templateHasClass = (x) =>
        String(x.class_id) === String(class_id) ||
        (Array.isArray(x.class_ids) && x.class_ids.some((id) => String(id) === String(class_id))) ||
        (Array.isArray(x.classes) && x.classes.some((item) => String(item?.id) === String(class_id)));

      const primaryClassTemplate = list.find(
        (x) =>
          isPrimaryClass &&
          templateHasClass(x) &&
          x.template_key === "primary_section_report_card"
      );

      const exactClassDefaultTemplate = list.find(
        (x) =>
          templateHasClass(x) &&
          (x.is_default === true || Number(x.is_default) === 1)
      );

      const exactClassVisualTemplate = list.find(
        (x) => templateHasClass(x) && hasVisualReportCardLayout(x)
      );

      const globalDefaultTemplate = list.find(
        (x) =>
          !x.class_id &&
          !(Array.isArray(x.class_ids) && x.class_ids.length) &&
          !(Array.isArray(x.classes) && x.classes.length) &&
          (x.is_default === true || Number(x.is_default) === 1)
      );

      const defaultTemplate =
        primaryClassTemplate ||
        exactClassDefaultTemplate ||
        exactClassVisualTemplate ||
        globalDefaultTemplate ||
        list[0];

      setSelectedTemplateId(defaultTemplate?.id ? String(defaultTemplate.id) : "");
    } catch (error) {
      console.error("Failed to load report card templates:", error);
      setReportTemplates([]);
      setSelectedTemplateId("");
      Swal.fire("Error", "Failed to load report card templates", "error");
    } finally {
      setLoadingTemplates(false);
    }
  };

  const selectAllComponentsTermWise = (availableComponents = []) => {
    const selected = {};
    for (const c of availableComponents) {
      const t = String(c.term_id);
      if (!selected[t]) selected[t] = [];
      if (!selected[t].includes(c.component_id)) selected[t].push(c.component_id);
    }
    return selected;
  };

  const loadSubjectComponentsAuto = async (class_id, subject_id, session_id) => {
    const params = { class_id, subject_id };
    if (session_id) params.session_id = Number(session_id);

    const res = await api.get("/exam-schemes/components/term-wise", {
      params,
    });
    const availableComponents = res.data || [];
    const selected_components = selectAllComponentsTermWise(availableComponents);
    return { availableComponents, selected_components };
  };

  const preselectAllSubjectsAndComponents = async (class_id, session_id, subjectsList) => {
    const rows = await Promise.all(
      (subjectsList || []).map(async (s) => {
        try {
          const { availableComponents, selected_components } = await loadSubjectComponentsAuto(
            class_id,
            s.id,
            session_id
          );
          return { subject_id: String(s.id), availableComponents, selected_components };
        } catch (e) {
          console.error("Failed to load components for subject:", s?.id, e);
          return { subject_id: String(s.id), availableComponents: [], selected_components: {} };
        }
      })
    );

    return rows.length
      ? rows
      : [{ subject_id: "", selected_components: {}, availableComponents: [] }];
  };

  const handleSessionChange = async (e) => {
    const session_id = e.target.value;

    setFilters({
      session_id,
      class_id: "",
      section_id: "",
      exam_ids: [],
      subjectComponents: [{ subject_id: "", selected_components: {}, availableComponents: [] }],
    });

    setStudentInfoMap({});
    setCoScholasticByTerm({});
    setRemarksByTerm({});
    setAttendanceByTerm({});
    setPromotionDecisionByTerm({});
    setReportFormat(null);
    setReportFormats([]);
    setSelectedReportFormatId("");
    setReportTemplates([]);
    setSelectedTemplateId("");

    setPdfMode("all");
    setPdfSingleId("");
    setPdfFrom("");
    setPdfTo("");
  };

  const handleClassChange = async (e) => {
    const class_id = e.target.value;

    setFilters((prev) => ({
      session_id: prev.session_id,
      class_id,
      section_id: "",
      exam_ids: [],
      subjectComponents: [{ subject_id: "", selected_components: {}, availableComponents: [] }],
    }));

    setSubjects([]);
    setReportData([]);
    setStudentInfoMap({});
    setCoScholasticByTerm({});
    setRemarksByTerm({});
    setAttendanceByTerm({});
    setPromotionDecisionByTerm({});
    setReportFormat(null);
    setReportFormats([]);
    setSelectedReportFormatId("");
    setReportTemplates([]);
    setSelectedTemplateId("");

    setPdfMode("all");
    setPdfSingleId("");
    setPdfFrom("");
    setPdfTo("");

    if (!class_id) {
      setReportFormat(null);
      return;
    }

    try {
      const sessionId = filters.session_id || undefined;
      const subjectList = await loadSubjects(class_id, sessionId);
      const subjectComponents = await preselectAllSubjectsAndComponents(class_id, sessionId, subjectList);

      setFilters((prev) => ({ ...prev, class_id, subjectComponents }));

      await loadReportCardFormats(class_id);
      await loadReportCardTemplates(class_id, sessionId);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to prepare subject/components", "error");
    }
  };

  const handleSubjectChange = async (e, index) => {
    const subject_id = e.target.value;

    if (!filters.session_id) {
      Swal.fire("Select Session", "Please select session first", "warning");
      return;
    }

    if (!filters.class_id) {
      Swal.fire("Select Class", "Please select class first", "warning");
      return;
    }

    try {
      const { availableComponents, selected_components } = await loadSubjectComponentsAuto(
        filters.class_id,
        subject_id,
        filters.session_id
      );

      setFilters((prev) => {
        const updated = [...prev.subjectComponents];
        updated[index] = { subject_id, availableComponents, selected_components };
        return { ...prev, subjectComponents: updated };
      });
    } catch {
      Swal.fire("Error", "Failed to load components", "error");
    }
  };

  const handleComponentToggle = (term_id, compId, index, checked) => {
    setFilters((prev) => {
      const updated = [...prev.subjectComponents];
      const selected = { ...(updated[index].selected_components || {}) };
      const t = String(term_id);

      if (!selected[t]) selected[t] = [];
      if (checked) {
        if (!selected[t].includes(compId)) selected[t] = [...selected[t], compId];
      } else {
        selected[t] = selected[t].filter((id) => id !== compId);
      }

      updated[index].selected_components = selected;
      return { ...prev, subjectComponents: updated };
    });
  };

  const addSubject = () =>
    setFilters((prev) => ({
      ...prev,
      subjectComponents: [
        ...prev.subjectComponents,
        { subject_id: "", selected_components: {}, availableComponents: [] },
      ],
    }));

  const removeSubject = (index) =>
    setFilters((prev) => {
      const next = prev.subjectComponents.filter((_, i) => i !== index);
      return {
        ...prev,
        subjectComponents: next.length
          ? next
          : [{ subject_id: "", selected_components: {}, availableComponents: [] }],
      };
    });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const getSelectedTermIds = () => {
    const ids = (filters.exam_ids || [])
      .map((exId) => exams.find((e) => e.id === exId)?.term_id)
      .filter(Boolean)
      .map((x) => Number(x));

    const unique = Array.from(new Set(ids)).sort((a, b) => a - b);
    return unique.slice(0, 2);
  };

  const termIds = useMemo(() => getSelectedTermIds(), [filters.exam_ids, exams]);
  const term1Id = termIds[0] ?? null;
  const term2Id = termIds[1] ?? null;

  const termLabel = (tid) => (tid ? `Term-${tid}` : "Term");

  const fetchAttendanceSummary = async ({ session_id, class_id, section_id, term_id }) => {
    const endpoints = [
      "/report-card/attendance-summary",
      "/attendance-entry/summary",
      "/student-term-attendances/summary",
      "/student-term-attendance/summary",
    ];

    let lastErr = null;

    for (const url of endpoints) {
      try {
        const params = { class_id, section_id, term_id };
        if (session_id) params.session_id = Number(session_id);
        const res = await api.get(url, { params });

        const attendanceMap = {};

        if (res?.data?.attendanceMap && typeof res.data.attendanceMap === "object") {
          Object.entries(res.data.attendanceMap).forEach(([sid, obj]) => {
            attendanceMap[Number(sid)] = { student_id: Number(sid), ...(obj || {}) };
          });
        } else if (Array.isArray(res?.data?.attendance)) {
          for (const a of res.data.attendance) attendanceMap[a.student_id] = a;
        } else if (Array.isArray(res?.data)) {
          for (const a of res.data) attendanceMap[a.student_id] = a;
        }

        return attendanceMap;
      } catch (e) {
        lastErr = e;
        const status = e?.response?.status;
        if (status !== 404) break;
      }
    }

    throw lastErr || new Error("Attendance summary API failed");
  };

  const getUniqueComponentsByTerm = (termId) => {
    if (!termId) return [];
    const compMap = new Map();

    (filters.subjectComponents || []).forEach((sc) => {
      const selectedIds = sc.selected_components?.[String(termId)] || [];
      const comps = (sc.availableComponents || []).filter(
        (c) => Number(c.term_id) === Number(termId) && selectedIds.includes(c.component_id)
      );

      comps.forEach((c) => {
        if (!compMap.has(c.component_id)) {
          const shortLabel =
            c.abbreviation ||
            c.abbr ||
            c.short_name ||
            c.shortName ||
            c.code ||
            c.component?.abbreviation ||
            c.component?.abbr ||
            c.component?.short_name ||
            c.component?.shortName ||
            c.component?.code;

          compMap.set(c.component_id, {
            component_id: c.component_id,
            label: shortLabel || c.name || c.component_name || c.componentName || "-",
          });
        }
      });
    });

    return Array.from(compMap.values());
  };

  const term1Components = useMemo(
    () => getUniqueComponentsByTerm(term1Id),
    [term1Id, filters.subjectComponents]
  );
  const term2Components = useMemo(
    () => getUniqueComponentsByTerm(term2Id),
    [term2Id, filters.subjectComponents]
  );

  const isCompInTerm = (c, termId) => {
    if (!termId) return false;
    const exTerm = exams.find((e) => e.id === c.exam_id)?.term_id;
    return Number(exTerm) === Number(termId);
  };

 const getSubjectTermCompDisplay = (student, subjectName, termId, componentId) => {
  const items = (student.components || []).filter(
    (c) =>
      c.subject_name === subjectName &&
      isCompInTerm(c, termId) &&
      Number(c.component_id) === Number(componentId)
  );

  if (!items.length) return "-";

  const hasAbsent = items.some((x) => {
    const att = String(x?.attendance || "").trim().toUpperCase();
    return ["A", "AB", "ABSENT"].includes(att);
  });

  if (hasAbsent) return "AB";

  if (items.some((x) => isNumeric(x?.marks))) {
    return items.reduce((a, x) => a + (isNumeric(x?.marks) ? Number(x.marks) : 0), 0);
  }

  const g = items.find((x) => x?.grade != null && String(x.grade).trim() !== "");
  return g?.grade || "-";
};

  const getSubjectComponentCount = (student, subjectName) => {
    const ids = new Set();
    (student?.components || []).forEach((c) => {
      if (c.subject_name !== subjectName) return;
      if (term1Id && isCompInTerm(c, term1Id)) ids.add(Number(c.component_id));
      if (term2Id && isCompInTerm(c, term2Id)) ids.add(Number(c.component_id));
    });
    return ids.size;
  };

  const getSubjectTermCompactComponents = (student, subjectName, termId) => {
    const map = new Map();
    (student?.components || [])
      .filter((c) => c.subject_name === subjectName && isCompInTerm(c, termId))
      .forEach((c) => {
        if (!map.has(Number(c.component_id))) {
          const label =
            c.abbreviation ||
            c.abbr ||
            c.short_name ||
            c.shortName ||
            c.code ||
            c.component?.abbreviation ||
            c.component?.abbr ||
            c.component?.short_name ||
            c.component?.shortName ||
            c.component?.code ||
            c.name ||
            c.component_name ||
            c.componentName ||
            "-";
          map.set(Number(c.component_id), {
            component_id: c.component_id,
            label,
          });
        }
      });
    return Array.from(map.values()).slice(0, 2);
  };

  const buildCompactScholasticPdfHtml = (student, subjects = []) => {
    const rows = [];
    subjects.forEach((subjectName) => {
      [term1Id, term2Id].filter(Boolean).forEach((termId) => {
        const comps = getSubjectTermCompactComponents(student, subjectName, termId);
        if (!comps.length) return;
        const stats = getSubjectTermStats(student, subjectName, termId);
        rows.push(`
          <tr>
            <td class="td-subject">${subjectName}</td>
            <td>${termLabel(termId)}</td>
            <td>${
              comps[0]
                ? `<div style="font-weight:800">${comps[0].label}</div><div>${getSubjectTermCompDisplay(student, subjectName, termId, comps[0].component_id)}</div>`
                : "-"
            }</td>
            <td>${
              comps[1]
                ? `<div style="font-weight:800">${comps[1].label}</div><div>${getSubjectTermCompDisplay(student, subjectName, termId, comps[1].component_id)}</div>`
                : "-"
            }</td>
            <td class="td-strong">${stats?.marksTotal != null ? stats.marksTotal : "-"}</td>
            <td class="td-strong">${stats?.grade || "-"}</td>
          </tr>
        `);
      });
    });

    if (!rows.length) return "";

    return `
      <table class="tbl compact-scholastic-table" style="margin-top:6px">
        <thead>
          <tr>
            <th class="th-subject">Subject</th>
            <th class="th-term">Term</th>
            <th class="th-comp">Component 1</th>
            <th class="th-comp">Component 2</th>
            <th class="th-comp strong">Marks Obtained</th>
            <th class="th-comp strong">Grade</th>
          </tr>
        </thead>
        <tbody>${rows.join("")}</tbody>
      </table>
    `;
  };

  const getSubjectTermStats = (student, subjectName, termId) => {
    const items = (student.components || []).filter(
      (c) => c.subject_name === subjectName && isCompInTerm(c, termId)
    );

    const marksTotal = hasAnyMarks(items) ? sumMarksOnly(items) : null;
    const hasWeightedScore = items.some((item) => isNumeric(item?.weighted_marks));
    const wTotal = sumWeightedOnly(items);
    const wMax = sumMaxWeight(items);
    const percent = hasWeightedScore && wMax > 0 ? (wTotal / wMax) * 100 : null;
    const grade = percent != null ? gradeFromSchema(percent, gradeSchema) : pickGrade(items);

    return { marksTotal, percent, grade };
  };

  const getStudentTermOverall = (student, termId) => {
    const items = (student.components || []).filter(
      (c) => isCompInTerm(c, termId) && !isCoScholasticComponent(c)
    );
    const wTotal = sumWeightedOnly(items);
    const wMax = sumMaxWeight(items);
    const percent = wMax > 0 ? (wTotal / wMax) * 100 : null;
    const grade = percent != null ? gradeFromSchema(percent, gradeSchema) : "-";
    return { total_weighted: wTotal, percent, grade };
  };

  const mergeCoScholasticSubjectRows = (areasMap, student) => {
    const normalizeName = (value) => String(value || "").trim().toLowerCase();
    const hasGrade = (row) => {
      const grade = String(row?.grade || "").trim();
      return grade && grade !== "-";
    };

    const existingByName = new Map();
    for (const [key, value] of areasMap.entries()) {
      const normalized = normalizeName(value?.area_name);
      if (normalized && !existingByName.has(normalized)) existingByName.set(normalized, key);
    }

    getCoScholasticSubjectNames(student).forEach((subjectName, index) => {
      const matching = (student?.components || []).find(
        (component) =>
          component.subject_name === subjectName && isCoScholasticComponent(component)
      );
      const subjectId = matching?.subject_id ?? matching?.subjectId ?? null;
      const t1Grade = term1Id ? getSubjectTermStats(student, subjectName, term1Id)?.grade || "-" : "-";
      const t2Grade = term2Id ? getSubjectTermStats(student, subjectName, term2Id)?.grade || "-" : "-";
      const normalized = normalizeName(subjectName);
      const existingKey = existingByName.get(normalized);

      if (existingKey !== undefined) {
        const current = areasMap.get(existingKey) || {};
        areasMap.set(existingKey, {
          ...current,
          area_name: current.area_name || subjectName,
          t1: hasGrade(current.t1) ? current.t1 : { grade: t1Grade },
          t2: hasGrade(current.t2) ? current.t2 : { grade: t2Grade },
        });
        return;
      }

      const key = `__subject__${subjectId || normalized || index}`;
      areasMap.set(key, {
        area_name: subjectName,
        t1: { grade: t1Grade },
        t2: { grade: t2Grade },
        serial_order: 10000 + index,
        source: "subject",
      });
      existingByName.set(normalized, key);
    });

    return areasMap;
  };

  // Build a stable, school-agnostic data object for Smart Report Card Templates.
  // The Template Studio maps visual fields to these paths once; every student reuses them.
  const buildSmartTemplateData = (student, infoOverride = {}) => {
    const info = infoOverride || {};
    const allComponents = Array.isArray(student?.components) ? student.components : [];
    const scholasticComponents = allComponents.filter((component) => !isCoScholasticComponent(component));
    const session = (sessions || []).find((item) => String(item.id) === String(filters.session_id)) || {};
    const className = info?.Class?.class_name || info?.class_name || "";
    const sectionName = info?.Section?.section_name || info?.section_name || "";
    const dobRaw = info?.Date_Of_Birth || info?.date_of_birth || info?.dob || "";
    const health = getPrimaryHealthInfo(info);

    const termBucket = (termId) => {
      const att = termId ? attendanceByTerm[String(termId)]?.[student.id] : null;
      const pct = buildAttendancePercent(att);
      return {
        present_days: att?.present_days ?? "",
        total_days: att?.total_days ?? "",
        display: buildPresentTotalText(att),
        percentage: pct == null ? "-" : Number(pct.toFixed ? pct.toFixed(2) : pct),
        percentage_text: pct == null ? "-" : `${Number(pct.toFixed ? pct.toFixed(2) : pct)}%`,
      };
    };

    const marks = {};
    const subjectData = {};
    const uniqueCombos = new Map();

    for (const c of allComponents) {
      const subjectName = c?.subject_name || c?.Subject?.name || c?.subject || "Subject";
      const subjectAliases = smartTemplateAliases(subjectName);
      const examTermId = Number(
        exams.find((e) => Number(e.id) === Number(c?.exam_id))?.term_id || c?.term_id || 0
      );
      const termKey =
        term1Id && examTermId === Number(term1Id)
          ? "term1"
          : term2Id && examTermId === Number(term2Id)
          ? "term2"
          : examTermId
          ? `term_${examTermId}`
          : "overall";
      const componentLabel =
        c?.abbreviation ||
        c?.abbr ||
        c?.short_name ||
        c?.shortName ||
        c?.code ||
        c?.component?.abbreviation ||
        c?.component?.abbr ||
        c?.component?.short_name ||
        c?.component?.shortName ||
        c?.component?.code ||
        c?.name ||
        c?.component_name ||
        c?.componentName ||
        `component_${c?.component_id || "x"}`;
      const componentAliases = smartTemplateAliases(componentLabel);
      const comboKey = `${subjectName}__${examTermId}__${c?.component_id || componentLabel}`;
      if (!uniqueCombos.has(comboKey)) {
        uniqueCombos.set(comboKey, { subjectName, subjectAliases, examTermId, termKey, componentAliases, componentId: c?.component_id });
      }
    }

    for (const combo of uniqueCombos.values()) {
      let display = "-";
      if (combo.componentId != null && combo.examTermId) {
        display = getSubjectTermCompDisplay(student, combo.subjectName, combo.examTermId, combo.componentId);
      } else {
        const rows = allComponents.filter((c) => c?.subject_name === combo.subjectName);
        const match = rows.find((c) => smartTemplateAliases(c?.abbreviation || c?.component_name || c?.name).some((x) => combo.componentAliases.includes(x)));
        const att = String(match?.attendance || "").trim().toUpperCase();
        display = ["A", "AB", "ABSENT"].includes(att) ? "AB" : (match?.marks ?? match?.grade ?? "-");
      }

      const matchingRows = allComponents.filter((c) => {
        if ((c?.subject_name || c?.subject) !== combo.subjectName) return false;
        const tid = Number(exams.find((e) => Number(e.id) === Number(c?.exam_id))?.term_id || c?.term_id || 0);
        if (combo.examTermId && tid !== combo.examTermId) return false;
        return combo.componentId == null || Number(c?.component_id) === Number(combo.componentId);
      });
      const first = matchingRows[0] || {};
      const cell = {
        display,
        marks: hasAnyMarks(matchingRows) ? sumMarksOnly(matchingRows) : (first?.marks ?? ""),
        max_marks: matchingRows.reduce((sum, row) => sum + (isNumeric(row?.max_marks) ? Number(row.max_marks) : 0), 0) || first?.max_marks || "",
        weighted_marks: sumWeightedOnly(matchingRows),
        grade: pickGrade(matchingRows),
        attendance: first?.attendance || "",
      };

      for (const subjectKey of combo.subjectAliases) {
        if (!marks[subjectKey]) marks[subjectKey] = {};
        if (!marks[subjectKey][combo.termKey]) marks[subjectKey][combo.termKey] = {};
        for (const componentKey of combo.componentAliases) {
          marks[subjectKey][combo.termKey][componentKey] = cell;
          // Also expose a non-term path for designs with a single exam/term.
          if (!marks[subjectKey][componentKey]) marks[subjectKey][componentKey] = cell;
        }
      }
    }

    const subjectNames = [...new Set(scholasticComponents.map((c) => c?.subject_name || c?.subject).filter(Boolean))];
    for (const subjectName of subjectNames) {
      const rows = scholasticComponents.filter((c) => (c?.subject_name || c?.subject) === subjectName);
      const raw = hasAnyMarks(rows) ? sumMarksOnly(rows) : null;
      const weighted = sumWeightedOnly(rows);
      const maxWeight = sumMaxWeight(rows);
      const hasWeightedScore = rows.some((row) => isNumeric(row?.weighted_marks));
      const pct = hasWeightedScore && maxWeight > 0 ? (weighted / maxWeight) * 100 : null;
      const entry = {
        name: subjectName,
        total_raw: raw ?? "-",
        total_weighted: weighted,
        percentage: pct == null ? "-" : Number(pct.toFixed(2)),
        grade: pct == null ? pickGrade(rows) : gradeFromSchema(pct, gradeSchema),
      };
      if (term1Id) entry.term1 = getSubjectTermStats(student, subjectName, term1Id);
      if (term2Id) entry.term2 = getSubjectTermStats(student, subjectName, term2Id);
      for (const key of smartTemplateAliases(subjectName)) subjectData[key] = entry;
    }

    const smartTableRows = subjectNames.map((subjectName) => {
      const subjectKey = smartTemplateAliases(subjectName)[0];
      const summary = subjectData[subjectKey] || { name: subjectName };
      return {
        key: subjectKey,
        name: subjectName,
        cells: marks[subjectKey] || {},
        total_raw: summary.total_raw ?? "-",
        total_weighted: summary.total_weighted ?? "-",
        percentage: summary.percentage ?? "-",
        grade: summary.grade ?? "-",
        term1: summary.term1 || {},
        term2: summary.term2 || {},
      };
    });

    const coScholastic = {};
    const addCoTerm = (termId, termKey) => {
      if (!termId) return;
      const bucket = coScholasticByTerm[String(termId)] || {};
      const rows = Object.values(bucket[String(student.id)] || {});
      for (const row of rows) {
        const areaName = row?.area_name || row?.name || `area_${row?.area_id || "x"}`;
        for (const key of smartTemplateAliases(areaName)) {
          if (!coScholastic[key]) coScholastic[key] = {};
          coScholastic[key][termKey] = row?.grade || row?.value || "-";
        }
      }
    };
    addCoTerm(term1Id, "term1");
    addCoTerm(term2Id, "term2");

    const coSubjectNames = getCoScholasticSubjectNames(student);
    for (const subjectName of coSubjectNames) {
      const t1Grade = term1Id ? getSubjectTermStats(student, subjectName, term1Id)?.grade || "-" : "-";
      const t2Grade = term2Id ? getSubjectTermStats(student, subjectName, term2Id)?.grade || "-" : "-";
      for (const key of smartTemplateAliases(subjectName)) {
        coScholastic[key] = {
          ...(coScholastic[key] || {}),
          term1: t1Grade,
          term2: t2Grade,
        };
      }
    }

    const drawingAlreadyHandled = coSubjectNames.some(isDrawingSubject);
    const drawingT1 = getDrawingGradeForTerm(student, term1Id, exams, gradeSchema);
    const drawingT2 = getDrawingGradeForTerm(student, term2Id, exams, gradeSchema);
    if (!drawingAlreadyHandled && (drawingT1 !== "-" || drawingT2 !== "-")) {
      coScholastic.drawing = { term1: drawingT1, term2: drawingT2 };
    }


    // SMART_TEMPLATE_CO_SCHOLASTIC_ROWS_V2
    // Inline/smart report-card templates use `co_scholastic.rows` for the
    // Co-Scholastic table. Keep the older `coscholastic.<slug>.term*` object as
    // well, but also expose real rows so Subject.Type = Co-Scholastic subjects
    // appear in Part-2 instead of only disappearing from Part-1.
    const coScholasticRowsByName = new Map();
    const normalizeCoRowName = (value) => String(value || "").trim().toLowerCase();
    const hasDisplayGrade = (value) => {
      const grade = String(value ?? "").trim();
      return Boolean(grade && grade !== "-");
    };

    const ensureCoRow = (name, serialOrder = 0, source = "area") => {
      const cleanName = String(name || "").trim();
      if (!cleanName) return null;
      const key = normalizeCoRowName(cleanName);
      if (!coScholasticRowsByName.has(key)) {
        coScholasticRowsByName.set(key, {
          name: cleanName,
          term1: "-",
          term2: "-",
          serial_order: Number(serialOrder || 0),
          source,
        });
      }
      return coScholasticRowsByName.get(key);
    };

    const collectMappedCoRows = (termId, termKey) => {
      if (!termId) return;
      const bucket = coScholasticByTerm[String(termId)] || {};
      const studentRows = Object.values(bucket[String(student.id)] || {});
      studentRows.forEach((row, index) => {
        const areaName = row?.area_name || row?.name || `Area ${index + 1}`;
        const target = ensureCoRow(
          areaName,
          row?.serial_order ?? row?.area_order ?? index,
          "area"
        );
        if (!target) return;
        const grade = row?.grade || row?.value || "-";
        if (hasDisplayGrade(grade) || !hasDisplayGrade(target[termKey])) {
          target[termKey] = grade;
        }
      });
    };

    collectMappedCoRows(term1Id, "term1");
    collectMappedCoRows(term2Id, "term2");

    coSubjectNames.forEach((subjectName, index) => {
      const target = ensureCoRow(subjectName, 10000 + index, "subject");
      if (!target) return;
      const t1Grade = term1Id
        ? getSubjectTermStats(student, subjectName, term1Id)?.grade || "-"
        : "-";
      const t2Grade = term2Id
        ? getSubjectTermStats(student, subjectName, term2Id)?.grade || "-"
        : "-";
      if (hasDisplayGrade(t1Grade) || !hasDisplayGrade(target.term1)) target.term1 = t1Grade;
      if (hasDisplayGrade(t2Grade) || !hasDisplayGrade(target.term2)) target.term2 = t2Grade;
    });

    if (!drawingAlreadyHandled && (drawingT1 !== "-" || drawingT2 !== "-")) {
      const target = ensureCoRow("Drawing", 9999, "drawing");
      if (target) {
        if (hasDisplayGrade(drawingT1)) target.term1 = drawingT1;
        if (hasDisplayGrade(drawingT2)) target.term2 = drawingT2;
      }
    }

    const smartCoScholasticRows = Array.from(coScholasticRowsByName.values())
      .sort((a, b) => Number(a.serial_order || 0) - Number(b.serial_order || 0))
      .map((row) => {
        // A one-term report card (for example Half-Yearly) has one Grade column.
        // If two terms are selected, prefer the later selected term while still
        // exposing both values for future/custom templates.
        const preferred = term2Id
          ? [row.term2, row.term1]
          : [row.term1, row.term2];
        const grade = preferred.find(hasDisplayGrade) || "-";
        return {
          name: row.name,
          grade,
          term1_grade: row.term1 || "-",
          term2_grade: row.term2 || "-",
          source: row.source,
        };
      });

    // SMCIS_DYNAMIC_CO_SCHOLASTIC_ROWS_V4
    // SMCIS Dynamic Part-2 reads `co_scholastic.rows`.
    // Build it from the Subject master FIRST (so a subject marked Co-Scholastic
    // still appears even if the detailed-summary component list is incomplete),
    // then merge the normal Co-Scholastic Area module rows and available grades.
    const dynamicCoRowsV4Map = new Map();
    const dynamicCoKeyV4 = (value) => String(value || "").trim().toLowerCase();
    const dynamicHasGradeV4 = (value) => {
      const grade = String(value ?? "").trim();
      return Boolean(grade && grade !== "-");
    };

    const dynamicPutCoRowV4 = (name, grade = "-", serialOrder = 0, source = "area") => {
      const cleanName = String(name || "").trim();
      if (!cleanName) return;
      const key = dynamicCoKeyV4(cleanName);
      const nextGrade = dynamicHasGradeV4(grade) ? String(grade).trim() : "-";
      const existing = dynamicCoRowsV4Map.get(key);

      if (!existing) {
        dynamicCoRowsV4Map.set(key, {
          name: cleanName,
          grade: nextGrade,
          serial_order: Number(serialOrder || 0),
          source,
        });
        return;
      }

      if (!dynamicHasGradeV4(existing.grade) && dynamicHasGradeV4(nextGrade)) {
        existing.grade = nextGrade;
      }
      if (Number(serialOrder || 0) < Number(existing.serial_order || 0)) {
        existing.serial_order = Number(serialOrder || 0);
      }
    };

    // 1) Existing Co-Scholastic Area evaluations (Drawing, Work Education, etc.).
    // For this SMCIS one-column Part-2 table, prefer the later selected term.
    const dynamicPreferredTermV4 = term2Id || term1Id || null;
    if (dynamicPreferredTermV4) {
      const bucket = coScholasticByTerm[String(dynamicPreferredTermV4)] || {};
      const areaRows = Object.values(bucket[String(student.id)] || {});
      areaRows.forEach((row, index) => {
        dynamicPutCoRowV4(
          row?.area_name || row?.name || `Activity ${index + 1}`,
          row?.grade || row?.value || "-",
          row?.serial_order ?? row?.area_order ?? index,
          "area"
        );
      });
    }

    // 2) Subject master is authoritative for Scholastic vs Co-Scholastic.
    // Also merge any component-derived names as a fallback for older responses.
    const dynamicCoSubjectCatalogV4 = new Map();
    (subjects || []).forEach((subject, index) => {
      if (normalizeSubjectType(subject?.type) !== "Co-Scholastic") return;
      const name = String(subject?.name || "").trim();
      if (!name) return;
      dynamicCoSubjectCatalogV4.set(dynamicCoKeyV4(name), {
        id: subject?.id ?? null,
        name,
        order: index,
      });
    });

    (coSubjectNames || []).forEach((name, index) => {
      const cleanName = String(name || "").trim();
      if (!cleanName) return;
      const key = dynamicCoKeyV4(cleanName);
      if (dynamicCoSubjectCatalogV4.has(key)) return;
      const matching = (student?.components || []).find(
        (component) => dynamicCoKeyV4(component?.subject_name || component?.subject) === key
      );
      dynamicCoSubjectCatalogV4.set(key, {
        id: matching?.subject_id ?? matching?.subjectId ?? null,
        name: cleanName,
        order: 1000 + index,
      });
    });

    Array.from(dynamicCoSubjectCatalogV4.values()).forEach((subjectMeta, index) => {
      const subjectName = subjectMeta.name;
      const subjectId = subjectMeta.id;
      const subjectKey = dynamicCoKeyV4(subjectName);
      const subjectRows = (student?.components || []).filter((component) => {
        const sameId =
          subjectId != null &&
          String(component?.subject_id ?? component?.subjectId ?? "") === String(subjectId);
        const sameName =
          dynamicCoKeyV4(component?.subject_name || component?.subject) === subjectKey;
        return sameId || sameName;
      });

      const t1Grade = term1Id
        ? getSubjectTermStats(student, subjectName, term1Id)?.grade || "-"
        : "-";
      const t2Grade = term2Id
        ? getSubjectTermStats(student, subjectName, term2Id)?.grade || "-"
        : "-";
      const directGrade = subjectRows
        .map(
          (row) =>
            row?.grade ??
            row?.weighted_grade ??
            row?.grade_obtained ??
            row?.grade_value
        )
        .find(dynamicHasGradeV4);
      const subjectGrade =
        subjectId != null
          ? student?.subject_grades?.[subjectId] ?? student?.subject_grades?.[String(subjectId)]
          : null;

      const preferredGrade = term2Id
        ? [t2Grade, t1Grade, directGrade, subjectGrade].find(dynamicHasGradeV4)
        : [t1Grade, t2Grade, directGrade, subjectGrade].find(dynamicHasGradeV4);

      // Always add a Subject-master Co-Scholastic row. If grade entry has not
      // been done yet it intentionally displays '-' instead of disappearing.
      dynamicPutCoRowV4(
        subjectName,
        preferredGrade || "-",
        10000 + Number(subjectMeta.order ?? index),
        "subject"
      );
    });

    // 3) Legacy Drawing fallback for old SMCIS records.
    if (!(coSubjectNames || []).some(isDrawingSubject)) {
      const drawingGrade = term2Id
        ? [drawingT2, drawingT1].find(dynamicHasGradeV4)
        : [drawingT1, drawingT2].find(dynamicHasGradeV4);
      if (dynamicHasGradeV4(drawingGrade)) {
        dynamicPutCoRowV4("Drawing", drawingGrade, 9999, "drawing");
      }
    }

    const dynamicCoScholasticRowsV4 = Array.from(dynamicCoRowsV4Map.values())
      .sort((a, b) => Number(a.serial_order || 0) - Number(b.serial_order || 0))
      .map(({ name, grade, source }) => ({ name, grade: grade || "-", source }));

    // SMCIS_DYNAMIC_ONLY_CO_SCHOLASTIC_V5
    // IMPORTANT: this block is intentionally limited to the coded SMCIS
    // Dynamic template. Other report-card templates keep their existing path.
    const smcisDynamicCoScholasticRowsV5 = (() => {
      if (selectedReportTemplate?.template_key !== "smcis_dynamic_term_report_card_v1") return [];

      const rowsMap = new Map();
      const keyOf = (value) => String(value || "").trim().toLowerCase();
      const validGrade = (value) => {
        const v = String(value ?? "").trim();
        return Boolean(v && v !== "-");
      };
      const putRow = (name, grade = "-", order = 0, source = "area") => {
        const cleanName = String(name || "").trim();
        if (!cleanName) return;
        const key = keyOf(cleanName);
        const next = {
          name: cleanName,
          grade: validGrade(grade) ? String(grade).trim() : "-",
          serial_order: Number(order || 0),
          source,
        };
        const old = rowsMap.get(key);
        if (!old) {
          rowsMap.set(key, next);
          return;
        }
        if (!validGrade(old.grade) && validGrade(next.grade)) old.grade = next.grade;
        if (Number(next.serial_order) < Number(old.serial_order || 0)) old.serial_order = next.serial_order;
      };

      // 1) Existing Co-Scholastic Area module rows (Drawing etc.).
      const displayTermId = term2Id || term1Id || null;
      if (displayTermId) {
        const bucket = coScholasticByTerm[String(displayTermId)] || {};
        const areaRows = Object.values(bucket[String(student.id)] || {});
        areaRows.forEach((row, index) => {
          putRow(
            row?.area_name || row?.name || `Activity ${index + 1}`,
            row?.grade || row?.value || "-",
            row?.serial_order ?? row?.area_order ?? index,
            "area"
          );
        });
      }

      // 2) Subjects explicitly marked Co-Scholastic in Subject Master.
      // This is the source needed for General Knowledge / Value Education etc.
      (subjects || []).forEach((subject, index) => {
        const type = String(subject?.type || "").trim().toLowerCase();
        if (type !== "co-scholastic" && type !== "coscholastic") return;

        const subjectName = String(subject?.name || "").trim();
        if (!subjectName) return;
        const subjectId = subject?.id;
        const subjectKey = keyOf(subjectName);
        const componentRows = (student?.components || []).filter((component) => {
          const byId = subjectId != null && String(component?.subject_id ?? component?.subjectId ?? "") === String(subjectId);
          const byName = keyOf(component?.subject_name || component?.subject) === subjectKey;
          return byId || byName;
        });

        const directGrade = pickGrade(componentRows);
        let termGrade = "-";
        if (displayTermId) {
          termGrade = getSubjectTermStats(student, subjectName, displayTermId)?.grade || "-";
        }
        const grade = validGrade(directGrade) ? directGrade : termGrade;

        // Always include the row. If grade entry is pending, show '-'.
        putRow(subjectName, grade, 10000 + index, "subject");
      });

      // 3) Old SMCIS data sometimes has Drawing only through the legacy helper.
      const drawingGrade = term2Id
        ? [drawingT2, drawingT1].find(validGrade)
        : [drawingT1, drawingT2].find(validGrade);
      if (validGrade(drawingGrade)) putRow("Drawing", drawingGrade, 9000, "drawing");

      return Array.from(rowsMap.values())
        .sort((a, b) => Number(a.serial_order || 0) - Number(b.serial_order || 0))
        .map(({ name, grade, source }) => ({ name, grade: grade || "-", source }));
    })();

    // SMCIS_CODED_CO_SCHOLASTIC_V6
    // This is ONLY the coded SMCIS inline-HTML report card. It does not change
    // Visual Designer layouts or other report-card templates.
    // Part-2 must contain BOTH:
    //   1) every Co-Scholastic Area mapped to the class (even if grade is pending),
    //   2) grade-only subjects whose Subject.type is Co-Scholastic.
    const smcisCodedCoScholasticRowsV6 = (() => {
      if (selectedReportTemplate?.template_key !== "smcis_dynamic_term_report_card_v1") return [];

      const rowsByName = new Map();
      const norm = (value) => String(value || "").trim().toLowerCase();
      const isRealGrade = (value) => {
        const v = String(value ?? "").trim();
        return Boolean(v && v !== "-");
      };
      const isCoType = (value) => {
        const v = norm(value).replace(/[_\s]+/g, "-");
        return v === "co-scholastic" || v === "coscholastic";
      };

      const ensureRow = (name, serialOrder = 0, source = "area") => {
        const cleanName = String(name || "").trim();
        if (!cleanName) return null;
        const key = norm(cleanName);
        let row = rowsByName.get(key);
        if (!row) {
          row = {
            name: cleanName,
            term1: "-",
            term2: "-",
            serial_order: Number(serialOrder || 0),
            source,
          };
          rowsByName.set(key, row);
        } else {
          const nextOrder = Number(serialOrder || 0);
          if (nextOrder && (!row.serial_order || nextOrder < Number(row.serial_order))) {
            row.serial_order = nextOrder;
          }
        }
        return row;
      };

      const mergeAreaTerm = (termId, termKey) => {
        if (!termId) return;
        const bucket = coScholasticByTerm[String(termId)] || {};

        // Seed ALL areas mapped for this class/term. This is the missing part in
        // the previous patches: `_areas` exists even when a student grade has not
        // yet been entered.
        const mappedAreas = Array.isArray(bucket?._areas)
          ? bucket._areas
          : Object.values(bucket?._areas || {});

        mappedAreas.forEach((area, index) => {
          ensureRow(
            area?.area_name || area?.name || `Activity ${index + 1}`,
            area?.serial_order ?? index,
            "mapped-area"
          );
        });

        // Overlay this student's saved values on top of the mapped rows.
        const studentRows = Object.values(bucket?.[String(student.id)] || {});
        studentRows.forEach((item, index) => {
          const target = ensureRow(
            item?.area_name || item?.name || `Activity ${index + 1}`,
            item?.serial_order ?? item?.area_order ?? index,
            "area-value"
          );
          if (!target) return;
          const grade = item?.grade || item?.value || "-";
          if (isRealGrade(grade) || !isRealGrade(target[termKey])) {
            target[termKey] = grade;
          }
        });
      };

      mergeAreaTerm(term1Id, "term1");
      mergeAreaTerm(term2Id, "term2");

      // Build a class-relevant Co-Scholastic subject catalogue. /subjects is a
      // global list, so only keep subjects that have assessment components for
      // this selected class OR are actually present in this student's result.
      const componentSubjectIds = new Set(
        (student?.components || [])
          .map((c) => c?.subject_id ?? c?.subjectId)
          .filter((id) => id != null && String(id).trim() !== "")
          .map((id) => String(id))
      );
      const componentSubjectNames = new Set(
        (student?.components || [])
          .map((c) => norm(c?.subject_name || c?.subject || c?.Subject?.name))
          .filter(Boolean)
      );
      const configuredSubjectIds = new Set(
        (filters?.subjectComponents || [])
          .filter((sc) => Array.isArray(sc?.availableComponents) && sc.availableComponents.length > 0)
          .map((sc) => String(sc?.subject_id || ""))
          .filter(Boolean)
      );

      const subjectCatalog = new Map();
      (subjects || []).forEach((subject, index) => {
        if (!isCoType(subject?.type)) return;
        const id = subject?.id != null ? String(subject.id) : "";
        const name = String(subject?.name || "").trim();
        if (!name) return;
        const relevant =
          (id && (componentSubjectIds.has(id) || configuredSubjectIds.has(id))) ||
          componentSubjectNames.has(norm(name));
        if (!relevant) return;
        subjectCatalog.set(norm(name), { id: subject?.id ?? null, name, order: index });
      });

      // Fallback to the detailed-summary metadata. This catches older data where
      // the Subject master list was not loaded yet but subject_type is present.
      (student?.components || []).forEach((component, index) => {
        if (!isCoType(component?.subject_type ?? component?.subjectType)) return;
        const name = String(component?.subject_name || component?.subject || component?.Subject?.name || "").trim();
        if (!name) return;
        const key = norm(name);
        if (!subjectCatalog.has(key)) {
          subjectCatalog.set(key, {
            id: component?.subject_id ?? component?.subjectId ?? null,
            name,
            order: 1000 + index,
          });
        }
      });

      Array.from(subjectCatalog.values()).forEach((meta, index) => {
        const target = ensureRow(meta.name, 10000 + Number(meta.order ?? index), "co-scholastic-subject");
        if (!target) return;

        const subjectRows = (student?.components || []).filter((component) => {
          const sameId =
            meta.id != null &&
            String(component?.subject_id ?? component?.subjectId ?? "") === String(meta.id);
          const sameName =
            norm(component?.subject_name || component?.subject || component?.Subject?.name) === norm(meta.name);
          return sameId || sameName;
        });

        const t1Grade = term1Id
          ? getSubjectTermStats(student, meta.name, term1Id)?.grade || "-"
          : "-";
        const t2Grade = term2Id
          ? getSubjectTermStats(student, meta.name, term2Id)?.grade || "-"
          : "-";
        const directGrade = subjectRows
          .map((row) => row?.grade ?? row?.weighted_grade ?? row?.grade_obtained ?? row?.grade_value)
          .find(isRealGrade);
        const storedSubjectGrade = meta.id != null
          ? student?.subject_grades?.[meta.id] ?? student?.subject_grades?.[String(meta.id)]
          : null;

        if (isRealGrade(t1Grade)) target.term1 = t1Grade;
        if (isRealGrade(t2Grade)) target.term2 = t2Grade;

        // If only one direct grade is available, use it for the report's selected
        // term rather than hiding the subject.
        const fallbackGrade = [directGrade, storedSubjectGrade].find(isRealGrade);
        if (isRealGrade(fallbackGrade)) {
          if (term2Id && !isRealGrade(target.term2)) target.term2 = fallbackGrade;
          else if (!isRealGrade(target.term1)) target.term1 = fallbackGrade;
        }
      });

      // Legacy SMCIS Drawing calculation: add only when no mapped/subject row
      // with the same name already exists.
      const drawingT1V6 = getDrawingGradeForTerm(student, term1Id, exams, gradeSchema);
      const drawingT2V6 = getDrawingGradeForTerm(student, term2Id, exams, gradeSchema);
      if (isRealGrade(drawingT1V6) || isRealGrade(drawingT2V6)) {
        const drawing = ensureRow("Drawing", 9000, "legacy-drawing");
        if (drawing) {
          if (isRealGrade(drawingT1V6) && !isRealGrade(drawing.term1)) drawing.term1 = drawingT1V6;
          if (isRealGrade(drawingT2V6) && !isRealGrade(drawing.term2)) drawing.term2 = drawingT2V6;
        }
      }

      return Array.from(rowsByName.values())
        .sort((a, b) => Number(a.serial_order || 0) - Number(b.serial_order || 0))
        .map((row) => {
          const preferred = term2Id ? [row.term2, row.term1] : [row.term1, row.term2];
          return {
            name: row.name,
            grade: preferred.find(isRealGrade) || "-",
            source: row.source,
          };
        });
    })();

    const weightedTotal = sumWeightedOnly(scholasticComponents);
    const maxWeightTotal = sumMaxWeight(scholasticComponents);
    const overallPct = maxWeightTotal > 0 ? (weightedTotal / maxWeightTotal) * 100 : null;
    const rawTotal = hasAnyMarks(scholasticComponents) ? sumMarksOnly(scholasticComponents) : null;
    const rawMax = scholasticComponents.reduce((sum, row) => sum + (isNumeric(row?.max_marks) ? Number(row.max_marks) : 0), 0);
    const term1Overall = term1Id ? getStudentTermOverall(student, term1Id) : null;
    const term2Overall = term2Id ? getStudentTermOverall(student, term2Id) : null;
    const r1 = term1Id ? remarksByTerm[String(term1Id)]?.[student.id] : null;
    const r2 = term2Id ? remarksByTerm[String(term2Id)]?.[student.id] : null;
    const promotion = term2Id ? promotionDecisionByTerm[String(term2Id)]?.[student.id] : null;

    return {
      school: {
        name:
          getReportCardFormatValue("school_name") ||
          getReportCardFormatValue("institution_name") ||
          selectedReportTemplate?.school_name ||
          "",
        logo_url: getReportCardSchoolLogoUrl() || "",
      },
      smart_table: {
        rows: smartTableRows,
      },
      student: {
        id: student.id,
        name: info?.name || info?.student_name || student?.name || "-",
        admission_number: info?.admission_number || info?.AdmissionNumber || student?.admission_number || "-",
        roll_number: info?.roll_number ?? student?.roll_number ?? "-",
        class_name: className || "-",
        section_name: sectionName || "-",
        class_section: [className, sectionName].filter(Boolean).join(" - ") || "-",
        father_name: info?.father_name || "-",
        mother_name: info?.mother_name || "-",
        dob: formatDOB(dobRaw),
        blood_group: info?.blood_group_snapshot || info?.blood_group || info?.b_group || "-",
        photo_data_url: info?.__pdfPhotoSrc || getStudentPhotoURL(info) || "",
      },
      session: {
        id: session?.id || filters.session_id || null,
        name: session?.name || "-",
      },
      result: {
        total_raw: rawTotal ?? "-",
        max_raw: rawMax || "-",
        total_weighted: Number(weightedTotal.toFixed ? weightedTotal.toFixed(2) : weightedTotal),
        max_weighted: Number(maxWeightTotal.toFixed ? maxWeightTotal.toFixed(2) : maxWeightTotal),
        percentage: overallPct == null ? "-" : Number(overallPct.toFixed(2)),
        percentage_text: overallPct == null ? "-" : `${Number(overallPct.toFixed(2))}%`,
        grade: overallPct == null ? "-" : gradeFromSchema(overallPct, gradeSchema),
        rank: hasDisplayRank(student?.rank) ? student.rank : "-",
        term1: term1Overall || {},
        term2: term2Overall || {},
      },
      attendance: {
        term1: termBucket(term1Id),
        term2: termBucket(term2Id),
      },
      remarks: {
        term1: r1 || "-",
        term2: r2 || "-",
        final: r2 || r1 || "-",
      },
      health,
      promotion: promotion || {},
      marks,
      subjects: subjectData,
      coscholastic: coScholastic,
      co_scholastic: selectedReportTemplate?.template_key === "smcis_dynamic_term_report_card_v1"
        ? { rows: smcisCodedCoScholasticRowsV6 }
        : { rows: dynamicCoScholasticRowsV4 },
    };
  };

  const fetchReport = async () => {
    const { session_id, class_id, section_id, exam_ids } = filters;
    if (!session_id || !class_id || !section_id || !exam_ids.length) {
      return Swal.fire("Missing Field", "Select session, class, section & exam(s)", "warning");
    }

    setLoading(true);

    const subjectComponentsPayload = (filters.subjectComponents || [])
      .filter((sc) => sc.subject_id)
      .map((sc) => ({
        subject_id: Number(sc.subject_id),
        selected_components: sc.selected_components || {},
      }))
      .filter((x) => x.subject_id);

    const payload = {
      session_id: +session_id,
      class_id: +class_id,
      section_id: +section_id,
      exam_ids,
      subjectComponents: subjectComponentsPayload,
      sum: true,
      showSubjectTotals: true,
      includeGrades: true,
    };

    try {
      const res = await api.post("/report-card/detailed-summary", payload);
      const subjectTypeById = new Map(
        (subjects || []).map((subject) => [String(subject.id), normalizeSubjectType(subject.type)])
      );
      const subjectTypeByName = new Map(
        (subjects || []).map((subject) => [String(subject.name || "").trim().toLowerCase(), normalizeSubjectType(subject.type)])
      );
      const reportStudents = (res.data.students || []).map((student) => ({
        ...student,
        components: (student?.components || []).map((component) => ({
          ...component,
          subject_type: normalizeSubjectType(
            component?.subject_type ??
              component?.subjectType ??
              subjectTypeById.get(String(component?.subject_id ?? component?.subjectId ?? "")) ??
              subjectTypeByName.get(String(component?.subject_name || "").trim().toLowerCase())
          ),
        })),
      }));

      if (!reportStudents.length) {
        Swal.fire("No Data", "No students found for the selected filters", "info");
        setReportData([]);
        setStudentInfoMap({});
        setCoScholasticByTerm({});
        setRemarksByTerm({});
        setAttendanceByTerm({});
        setPromotionDecisionByTerm({});
        setLoading(false);
        return;
      }

      setReportData(reportStudents);

      setPdfMode("all");
      setPdfSingleId("");
      setPdfFrom("");
      setPdfTo("");

      const studentIds = reportStudents.map((s) => s.id);

      const infoRes = await api.get("/report-card/students", {
        params: {
          session_id: Number(session_id),
          class_id: Number(class_id),
          section_id: Number(section_id),
          student_ids: studentIds,
        },
      });
      const studentMap = {};
      for (const s of infoRes.data.students || []) studentMap[s.id] = s;

      // ✅ /report-card/students sometimes does not include photo/photo_url.
      // Pull student rows from /students/searchByClassAndSection and merge only missing photo fields.
      try {
        const fallbackLookup = await fetchStudentFallbackLookupForClass({
          session_id,
          class_id,
          section_id,
        });

        for (const stu of reportStudents) {
          studentMap[stu.id] = mergeStudentInfoWithFallback(stu, studentMap[stu.id] || {}, fallbackLookup);
        }
      } catch (photoMergeError) {
        console.warn("Student photo fallback merge skipped:", photoMergeError);
      }

      console.log("✅ REPORT CARD STUDENT SAMPLE:", Object.values(studentMap)[0]);
      setStudentInfoMap(studentMap);

      const termIdsLocal = getSelectedTermIds();

   const coByTerm = {};
      for (const tid of termIdsLocal.slice(0, 2)) {
        try {
          console.log("CoScholastic request", {
            session_id,
            class_id,
            section_id,
            term_id: tid,
          });

          const coRes = await api.get("/report-card/coscholastic-summary", {
            params: {
              session_id: Number(session_id),
              class_id: Number(class_id),
              section_id: Number(section_id),
              term_id: Number(tid),
            },
          });

          console.log("CoScholastic response", coRes.data);

        coByTerm[String(tid)] =
          coRes?.data && typeof coRes.data === "object" ? coRes.data : {};
        } catch (e) {
          console.warn("Co-scholastic failed for term", tid, e);
          coByTerm[String(tid)] = [];
        }
      }
      setCoScholasticByTerm(coByTerm);

      const remarksTermMap = {};
      for (const tid of termIdsLocal.slice(0, 2)) {
        try {
          const remarksRes = await api.get("/report-card/remarks-summary", {
            params: { session_id, class_id, section_id, term_id: tid },
          });

          const rm = {};
          for (const r of remarksRes.data.remarks || []) {
            const sid = r.student_id ?? r.studentId ?? r?.student?.id;
            const val = r.remark ?? r.remarks ?? r.text ?? r.comment ?? "";
            if (sid) rm[Number(sid)] = (val || "").trim() || "-";
          }
          remarksTermMap[String(tid)] = rm;
        } catch (e) {
          console.warn("Remarks failed for term", tid, e);
          remarksTermMap[String(tid)] = {};
        }
      }
      setRemarksByTerm(remarksTermMap);
            const promotionTermMap = {};
      for (const tid of termIdsLocal.slice(0, 2)) {
        try {
          const promoRes = await api.get("/student-promotion-decisions", {
            params: {
              session_id: Number(session_id),
              class_id: Number(class_id),
              section_id: Number(section_id),
              term_id: Number(tid),
            },
          });

          const pm = {};
        for (const r of promoRes.data.existingDecisions || []) {
          const sid = r.student_id ?? r.studentId ?? r?.student?.id;
          if (sid) {
            pm[Number(sid)] = {
              promotion_status: r.promotion_status || "",
              promoted_to_class_id: r.promoted_to_class_id ?? null,
              promoted_to_class_name:
                r.promotedToClass?.class_name ||
                r.promoted_to_class_name ||
                "",
              current_class_id: r.class_id ?? null,
              promotion_date: r.promotion_date || null,
            };
          }
        }
          promotionTermMap[String(tid)] = pm;
        } catch (e) {
          console.warn("Promotion decision failed for term", tid, e);
          promotionTermMap[String(tid)] = {};
        }
      }
      setPromotionDecisionByTerm(promotionTermMap);

      const attTermMap = {};
      for (const tid of termIdsLocal.slice(0, 2)) {
        try {
          const attendanceMap = await fetchAttendanceSummary({
            session_id,
            class_id,
            section_id,
            term_id: tid,
          });
          attTermMap[String(tid)] = attendanceMap || {};
        } catch (e) {
          console.warn("Attendance failed for term", tid, e);
          attTermMap[String(tid)] = {};
        }
      }
      setAttendanceByTerm(attTermMap);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to fetch report data", "error");
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (value) => {
    if (value == null || isNaN(value)) return "-";
    let num = +value;
    const pow = 10 ** numberFormat.decimalPoints;
    if (numberFormat.rounding === "floor") num = Math.floor(num * pow) / pow;
    if (numberFormat.rounding === "ceiling") num = Math.ceil(num * pow) / pow;
    return num.toFixed(numberFormat.decimalPoints);
  };

  const formatPercent = (p) => (p != null ? `${formatNumber(p)}%` : "-");

  const buildScholasticHeaderHtml_TermWise = () => {
    const t1Cols = (term1Components?.length || 0) + (showTotals ? 2 : 0);
    const t2Cols = (term2Components?.length || 0) + (showTotals ? 2 : 0);
    const gCols = showTotals ? 2 : 0;

    const top = `
      <tr>
        <th rowspan="2" class="th-subject">Subject</th>
        <th colspan="${t1Cols}" class="th-term">${term1Id ? termLabel(term1Id) : "Term-I"}</th>
        <th colspan="${t2Cols}" class="th-term">${term2Id ? termLabel(term2Id) : "Term-II"}</th>
        ${showTotals ? `<th colspan="${gCols}" class="th-grand">Grand Total</th>` : ""}
      </tr>
    `;

    const bottom = `
      <tr>
        ${term1Components.map((c) => `<th class="th-comp">${c.label}</th>`).join("")}
        ${
          showTotals
            ? `<th class="th-comp strong">Total</th><th class="th-comp strong">Grade</th>`
            : ""
        }

        ${term2Components.map((c) => `<th class="th-comp">${c.label}</th>`).join("")}
        ${
          showTotals
            ? `<th class="th-comp strong">Total</th><th class="th-comp strong">Grade</th>`
            : ""
        }

        ${
          showTotals
            ? `<th class="th-comp strong">Total</th><th class="th-comp strong">Grade</th>`
            : ""
        }
      </tr>
    `;

    return top + bottom;
  };

  const buildScholasticBodyRowHtml_TermWise = (student, subjectName) => {
    const s1 = term1Id ? getSubjectTermStats(student, subjectName, term1Id) : null;
    const s2 = term2Id ? getSubjectTermStats(student, subjectName, term2Id) : null;

    const allSubj = (student.components || []).filter((c) => c.subject_name === subjectName);
    const grandMarks = hasAnyMarks(allSubj) ? sumMarksOnly(allSubj) : null;

    const gW = sumWeightedOnly(allSubj);
    const gMax = sumMaxWeight(allSubj);
    const gPct = gMax > 0 ? (gW / gMax) * 100 : null;
    const grandGrade = gPct != null ? gradeFromSchema(gPct, gradeSchema) : pickGrade(allSubj);

    let row = `<tr><td class="td-subject">${subjectName}</td>`;

    for (const c of term1Components) {
      const val = term1Id
        ? getSubjectTermCompDisplay(student, subjectName, term1Id, c.component_id)
        : "-";
      row += `<td>${val}</td>`;
    }
    if (showTotals) {
      row += `<td class="td-strong">${s1?.marksTotal != null ? s1.marksTotal : "-"}</td>`;
      row += `<td class="td-strong">${s1?.grade || "-"}</td>`;
    }

    for (const c of term2Components) {
      const val = term2Id
        ? getSubjectTermCompDisplay(student, subjectName, term2Id, c.component_id)
        : "-";
      row += `<td>${val}</td>`;
    }
    if (showTotals) {
      row += `<td class="td-strong">${s2?.marksTotal != null ? s2.marksTotal : "-"}</td>`;
      row += `<td class="td-strong">${s2?.grade || "-"}</td>`;
    }

    if (showTotals) {
      row += `<td class="td-strong">${grandMarks != null ? grandMarks : "-"}</td>`;
      row += `<td class="td-strong">${grandGrade || "-"}</td>`;
    }

    row += `</tr>`;
    return row;
  };

  const getScholasticColumnCount = () => {
    return (
      1 +
      term1Components.length +
      (showTotals ? 2 : 0) +
      term2Components.length +
      (showTotals ? 2 : 0) +
      (showTotals ? 2 : 0)
    );
  };

  const buildTotalsFooterRowHtml = (student) => {
    if (!showTotals) return "";

    const t1 = term1Id ? getStudentTermOverall(student, term1Id) : null;
    const t2 = term2Id ? getStudentTermOverall(student, term2Id) : null;

    const grandTotal = student?.total_weighted;
    const grandPct = student?.grand_percent_weighted;

    const computedGrandGradeRaw =
      student?.total_grade_weighted ||
      (grandPct != null ? gradeFromSchema(grandPct, gradeSchema) : null);

    const computedGrandGrade =
      computedGrandGradeRaw && String(computedGrandGradeRaw).trim() !== "-"
        ? String(computedGrandGradeRaw).trim()
        : null;

      const blank1 =
        term1Components.length > 0
          ? `<td colspan="${term1Components.length}" style="color:#0b1b3a !important;"></td>`
          : "";

      const blank2 =
        term2Components.length > 0
          ? `<td colspan="${term2Components.length}" style="color:#0b1b3a !important;"></td>`
          : "";

    const rankRow = hasDisplayRank(student?.rank)
      ? `
      <tr>
        <td
          class="td-rank-label"
          style="background:linear-gradient(180deg,#1e3a8a,#1e40af);color:#ffffff !important;font-weight:900;text-align:left;"
        >
          Rank
        </td>
        <td
          colspan="${getScholasticColumnCount() - 1}"
          class="td-rank-value"
          style="background:#fff7cc !important;color:#0b1b3a !important;font-weight:900;text-align:right !important;padding-right:12px !important;"
        >
          <span
            class="rank-highlight"
            style="display:inline-block;padding:1px 7px;border-radius:8px;background:rgba(255,243,199,0.95);border:1px solid rgba(251,191,36,0.55);color:#0b1b3a !important;font-size:11px;font-weight:900;line-height:1.1;"
          >
            ${student.rank}
          </span>
        </td>
      </tr>
    `
      : "";

    const grandGradeCell = `
    ${computedGrandGrade
      ? `<div style="font-weight:900;font-size:12px;color:#0b1b3a !important;">${computedGrandGrade}</div>`
      : ``}
    <div
      style="margin-top:1px;font-size:11px;font-weight:900;display:inline-block;padding:1px 7px;border-radius:8px;background:rgba(255,243,199,0.95);border:1px solid rgba(251,191,36,0.55);color:#0b1b3a !important;"
    >
      ${grandPct != null ? `${formatNumber(grandPct)}%` : "-"}
    </div>
  `;

    const term1PctCell = `
    <div style="font-weight:900;color:#0b1b3a !important;">
      ${formatPercent(t1?.percent)}
    </div>
  `;

    const term2PctCell = `
    <div style="font-weight:900;color:#0b1b3a !important;">
      ${formatPercent(t2?.percent)}
    </div>
  `;

    return `
    <tr>
      <td
        class="td-total-label"
        style="background:linear-gradient(180deg,#1e3a8a,#1e40af);color:#ffffff !important;font-weight:900;text-align:left;"
      >
        TOTAL
      </td>

      ${blank1}

      <td
        class="td-total"
        style="background:#ffe066 !important;color:#0b1b3a !important;font-weight:900;"
      >
        <div style="font-weight:900;font-size:11px;letter-spacing:0.1px;color:#0b1b3a !important;">
          ${t1 ? formatNumber(t1.total_weighted) : "-"}
        </div>
      </td>

      <td
        class="td-total"
        style="background:#ffe066 !important;color:#0b1b3a !important;font-weight:900;"
      >
        ${term1PctCell}
      </td>

      ${blank2}

      <td
        class="td-total"
        style="background:#ffe066 !important;color:#0b1b3a !important;font-weight:900;"
      >
        <div style="font-weight:900;font-size:11px;letter-spacing:0.1px;color:#0b1b3a !important;">
          ${t2 ? formatNumber(t2.total_weighted) : "-"}
        </div>
      </td>

      <td
        class="td-total"
        style="background:#ffe066 !important;color:#0b1b3a !important;font-weight:900;"
      >
        ${term2PctCell}
      </td>

      <td
        class="td-grand"
        style="background:#ffd43b !important;color:#0b1b3a !important;font-weight:900;"
      >
        <div style="font-weight:900;font-size:11px;letter-spacing:0.1px;color:#0b1b3a !important;">
          ${formatNumber(grandTotal)}
        </div>
      </td>

      <td
        class="td-grand"
        style="background:#ffd43b !important;color:#0b1b3a !important;font-weight:900;"
      >
        ${grandGradeCell}
      </td>
    </tr>
    ${rankRow}
  `;
  };

  const buildCoScholasticPdfHtml_TwoTerms = (student) => {
  const studentId = student?.id;
  const t1 = term1Id ? coScholasticByTerm[String(term1Id)] || {} : {};
  const t2 = term2Id ? coScholasticByTerm[String(term2Id)] || {} : {};

    const areasMap = new Map();
    const mappedAreas = [
      ...Object.values(t1._areas || {}),
      ...Object.values(t2._areas || {}),
    ];

    mappedAreas.forEach((area) => {
      if (!area?.area_id) return;
      areasMap.set(area.area_id, {
        area_name: area.area_name,
        t1: null,
        t2: null,
        serial_order: area.serial_order || 0,
      });
    });

  const s1 = Object.values(t1[String(studentId)] || {});
  const s2 = Object.values(t2[String(studentId)] || {});

    s1.forEach((g) =>
      areasMap.set(g.area_id, {
        ...(areasMap.get(g.area_id) || {}),
        area_name: g.area_name || areasMap.get(g.area_id)?.area_name,
        t1: g,
      })
    );

    s2.forEach((g) => {
      const prev = areasMap.get(g.area_id);
      if (prev) areasMap.set(g.area_id, { ...prev, t2: g });
      else areasMap.set(g.area_id, { area_name: g.area_name, t1: null, t2: g });
    });

    mergeCoScholasticSubjectRows(areasMap, student);

    const drawingAlreadyHandled = getCoScholasticSubjectNames(student).some(isDrawingSubject);
    const drawingT1 = getDrawingGradeForTerm(student, term1Id, exams, gradeSchema);
    const drawingT2 = getDrawingGradeForTerm(student, term2Id, exams, gradeSchema);

    if (!drawingAlreadyHandled && (drawingT1 !== "-" || drawingT2 !== "-")) {
      areasMap.set("__drawing__", {
        area_name: "Drawing",
        t1: { grade: drawingT1 },
        t2: { grade: drawingT2 },
      });
    }

    const rows = Array.from(areasMap.values()).sort(
      (a, b) => (a.serial_order || 0) - (b.serial_order || 0)
    );

    return `
      <div class="section-title">
        <div class="section-title-left">
          <div class="section-pill">Co-Scholastic</div>
          <h5 style="margin:0;color:#0b1b3a;font-size:10px">Co-Scholastic Area</h5>
        </div>
      </div>
      <table class="tbl">
        <thead>
          <tr>
            <th class="th-subject" style="text-align:left">Area</th>
            <th class="th-term">${term1Id ? termLabel(term1Id) : "Term-I"} Grade</th>
            <th class="th-term">${term2Id ? termLabel(term2Id) : "Term-II"} Grade</th>
          </tr>
        </thead>
        <tbody>
          ${
            rows.length
              ? rows
                  .map(
                    (r) => `
                      <tr>
                        <td style="text-align:left;font-weight:700">${r.area_name || "-"}</td>
                        <td>${r.t1?.grade || "-"}</td>
                        <td>${r.t2?.grade || "-"}</td>
                      </tr>
                    `
                  )
                  .join("")
              : `<tr><td colspan="3" class="text-center">No co-scholastic data available</td></tr>`
          }
        </tbody>
      </table>
    `;
  };

  const buildAttendancePdfHtml_TermWise = (studentId) => {
    const a1 = term1Id ? attendanceByTerm[String(term1Id)]?.[studentId] : null;
    const a2 = term2Id ? attendanceByTerm[String(term2Id)]?.[studentId] : null;

    const t1Text = buildPresentTotalText(a1);
    const t2Text = buildPresentTotalText(a2);

    const t1Pct = buildAttendancePercent(a1);
    const t2Pct = buildAttendancePercent(a2);

    return `
      <div class="section-title">
        <div class="section-title-left">
          <div class="section-pill">Attendance</div>
          <h5 style="margin:0;color:#0b1b3a;font-size:10px">Attendance</h5>
        </div>
      </div>
      <table class="tbl">
        <thead>
          <tr>
            <th class="th-term">${term1Id ? termLabel(term1Id) : "Term-I"}</th>
            <th class="th-term">${term2Id ? termLabel(term2Id) : "Term-II"}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <div style="font-weight:900">${t1Text}</div>
              <div class="muted" style="margin-top:1px;font-size:11px">${
                t1Pct != null ? `${formatNumber(t1Pct)}%` : "-"
              }</div>
            </td>
            <td>
              <div style="font-weight:900">${t2Text}</div>
              <div class="muted" style="margin-top:1px;font-size:11px">${
                t2Pct != null ? `${formatNumber(t2Pct)}%` : "-"
              }</div>
            </td>
          </tr>
        </tbody>
      </table>
    `;
  };

const buildTeacherRemarksPdfHtml_TermWise = (studentId) => {
  const r2 = term2Id ? remarksByTerm[String(term2Id)]?.[studentId] : null;
  const promotion = term2Id
    ? promotionDecisionByTerm[String(term2Id)]?.[studentId]
    : null;

  const showPromotionFields =
    promotion &&
    promotion.promotion_status === "PROMOTED" &&
    promotion.promoted_to_class_id &&
    Number(promotion.promoted_to_class_id) !== Number(promotion.current_class_id);

  return `
    <div class="section-title">
      <div class="section-title-left">
        <div class="section-pill">Remarks</div>
        <h5 style="margin:0;color:#0b1b3a;font-size:10px">Teacher's Remarks</h5>
      </div>
    </div>

    <div class="remarks-grid" style="display:grid;grid-template-columns:1fr;gap:8px;">
      <div class="remarks-card">
        <div class="remarks-body">         
          <div>${(r2 || "-").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</div>
        </div>
      </div>

      ${
  showPromotionFields
    ? `
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;">
    <div class="remarks-card">
      <div class="remarks-body" style="font-size:13px;line-height:1.45;color:#0b1b3a;">
        <span style="font-weight:800;color:#475569;">Promoted To Class:</span>
        <span style="font-weight:700;"> ${promotion.promoted_to_class_name || "-"}</span>
      </div>
    </div>

    <div class="remarks-card">
      <div class="remarks-body" style="font-size:13px;line-height:1.45;color:#0b1b3a;">
        <span style="font-weight:800;color:#475569;">Promotion Date:</span>
        <span style="font-weight:700;">
          ${promotion.promotion_date ? formatDisplayDate(promotion.promotion_date) : "-"}
        </span>
      </div>
    </div>
  </div>
`
    : ""
}
    </div>
  `;
};

  const getStudentsForPdf = () => {
    if (!reportData?.length) return [];

    if (pdfMode === "single") {
      const sid = Number(pdfSingleId);
      return sid ? reportData.filter((s) => Number(s.id) === sid) : [];
    }

    if (pdfMode === "range") {
      const a = Number(pdfFrom);
      const b = Number(pdfTo);
      if (!Number.isFinite(a) || !Number.isFinite(b)) return [];

      const minV = Math.min(a, b);
      const maxV = Math.max(a, b);

      return reportData.filter((stu) => {
        const info = studentInfoMap[stu.id] || {};
        const roll = Number(info?.roll_number);
        return Number.isFinite(roll) && roll >= minV && roll <= maxV;
      });
    }

    return reportData;
  };

  const isPrimarySectionTemplateActive = () => {
    const selectedFormatOrientation = String(
      selectedReportFormat?.orientation || reportFormatRef.current?.orientation || reportFormat?.orientation || ""
    ).toLowerCase();

    if (selectedFormatOrientation === "landscape") return true;
    if (selectedFormatOrientation === "portrait") return false;

    const isPrimaryClass = ["0", "1", "2", "3"].includes(String(filters.class_id));
    const selectedIsPrimary = selectedReportTemplate?.template_key === "primary_section_report_card";
    const classHasPrimaryTemplate = (reportTemplates || []).some(
      (t) =>
        t.template_key === "primary_section_report_card" &&
        String(t.class_id) === String(filters.class_id)
    );

    return selectedIsPrimary || (isPrimaryClass && classHasPrimaryTemplate);
  };

  const escapePrimaryHtml = (value) =>
    String(value ?? "-")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const primaryComponentFallbackNames = {
    english_communication_language_literacy: {
      OA: "Oral Assessment",
      ROP: "Recognition of Pictures",
      SCON: "Sentence Construction",
      VOC: "Vocabulary",
      REC: "Recitation",
      FI: "Fluency and Interaction",
    },
    cognitive_mathematical_development: {
      OA: "Oral Assessment",
      SMO: "Sorting, Matching and Ordering",
      SER: "Seriation",
      UQ: "Understanding Quantity",
      REC: "Recognition",
      MA: "Mental Ability",
    },
    social_development_self_help: {
      WT: "Working Together",
      SF: "Self Feeding",
      PD: "Personal Development",
      FCR: "Follows Classroom Rules",
      TCB: "Takes Care of Belongings",
      IW: "Independent Work",
      CHF: "Cleanliness and Hygiene",
      CONF: "Confidence",
      OBD: "Obedience",
      PUNC: "Punctuality",
    },
    understanding_of_environment: {
      OA: "Oral Assessment",
      STN: "Sense of Nature",
      ASI: "Awareness of Surroundings",
      FH: "Family and Home",
      AHH: "Animals, Homes and Habitats",
      IA: "Identification Activity",
    },
    physical_mobility_stamina: {
      PMS: "Physical Mobility and Stamina",
      NC: "Neuro-Muscular Coordination",
    },
    creative_development: {
      RTR: "Rhymes, Tunes and Rhythm",
      SIT: "Singing in Tune",
      HMI: "Hand-Muscle Integration",
      CR: "Creativity",
      DC: "Drawing and Colouring",
      HOWK: "Handwork",
      WRT: "Writing",
      READ: "Reading",
      ACT: "Activity",
      DR: "Drawing",
      SCTI: "Subject Conceptual Thinking and Interpretation",
      PROJ: "Project",
    },
    default: {
      OA: "Oral Assessment",
      ROP: "Recognition of Pictures",
      SCON: "Sentence Construction",
      VOC: "Vocabulary",
      REC: "Recognition",
      FI: "Fluency and Interaction",
      SMO: "Sorting, Matching and Ordering",
      SER: "Seriation",
      UQ: "Understanding Quantity",
      MA: "Mental Ability",
      WT: "Working Together",
      SF: "Self Feeding",
      PD: "Personal Development",
      FCR: "Follows Classroom Rules",
      TCB: "Takes Care of Belongings",
      IW: "Independent Work",
      CHF: "Cleanliness and Hygiene",
      CONF: "Confidence",
      OBD: "Obedience",
      PUNC: "Punctuality",
      STN: "Sense of Nature",
      ASI: "Awareness of Surroundings",
      FH: "Family and Home",
      AHH: "Animals, Homes and Habitats",
      IA: "Identification Activity",
      PMS: "Physical Mobility and Stamina",
      NC: "Neuro-Muscular Coordination",
      RTR: "Rhymes, Tunes and Rhythm",
      SIT: "Singing in Tune",
      HMI: "Hand-Muscle Integration",
      CR: "Creativity",
      DC: "Drawing and Colouring",
      HOWK: "Handwork",
      WRT: "Writing",
      READ: "Reading",
      ACT: "Activity",
      DR: "Drawing",
      SCTI: "Subject Conceptual Thinking and Interpretation",
      PROJ: "Project",
    },
  };

  const normalizePrimaryKey = (value = "") =>
    String(value || "")
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");

  const getNestedPrimaryValue = (obj, keys = []) => {
    for (const key of keys) {
      const value = key.split(".").reduce((acc, part) => acc?.[part], obj);
      if (value != null && String(value).trim() !== "") return String(value).trim();
    }
    return "";
  };

  const getPrimaryComponentName = (component) =>
    getNestedPrimaryValue(component, [
      "full_name",
      "fullName",
      "component_full_name",
      "componentFullName",
      "display_name",
      "displayName",
      "component_display_name",
      "componentDisplayName",
      "AssessmentComponent.full_name",
      "AssessmentComponent.fullName",
      "AssessmentComponent.display_name",
      "AssessmentComponent.displayName",
      "AssessmentComponent.component_full_name",
      "AssessmentComponent.componentFullName",
      "component.full_name",
      "component.fullName",
      "component.display_name",
      "component.displayName",
      "component_name",
      "componentName",
      "name",
      "AssessmentComponent.name",
      "component.name",
      "abbreviation",
    ]) || "Assessment";

  const getPrimaryComponentAbbreviation = (component, rawName = "") => {
    const explicit = getNestedPrimaryValue(component, [
      "abbreviation",
      "abbr",
      "short_name",
      "shortName",
      "code",
      "component_code",
      "componentCode",
      "AssessmentComponent.abbreviation",
      "AssessmentComponent.abbr",
      "AssessmentComponent.short_name",
      "AssessmentComponent.shortName",
      "AssessmentComponent.code",
      "component.abbreviation",
      "component.abbr",
      "component.short_name",
      "component.shortName",
      "component.code",
    ]);

    if (explicit) return explicit.toUpperCase();

    const s = String(rawName || "").trim();
    const firstToken = s.match(/^([A-Z]{1,8})(?=\s|\(|-|$)/);
    if (firstToken) return firstToken[1].toUpperCase();

    return "";
  };

  const getPrimaryFallbackComponentFullName = (abbr, subjectName) => {
    const code = String(abbr || "").trim().toUpperCase();
    if (!code) return "";

    const subjectKey = normalizePrimaryKey(subjectName);
    const subjectMap = primaryComponentFallbackNames[subjectKey] || {};
    return subjectMap[code] || primaryComponentFallbackNames.default[code] || "";
  };

  const getPrimaryComponentDisplayName = (component, subjectName) => {
    const rawName = getPrimaryComponentName(component);
    const abbr = getPrimaryComponentAbbreviation(component, rawName);
    const cleanedName = cleanPrimaryComponentName(rawName, subjectName);
    const isOnlyAbbreviation =
      abbr && cleanedName.toUpperCase() === abbr.toUpperCase();

    const fallbackFullName = getPrimaryFallbackComponentFullName(abbr, subjectName);
    const fullName = isOnlyAbbreviation ? fallbackFullName || cleanedName : cleanedName;

    return fullName || "Assessment";
  };

  const getAllSubjectsForPrimary = (student) =>
    Array.from(new Set((student?.components || []).map((c) => c.subject_name).filter(Boolean)));

  const escapeRegExp = (value = "") => String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const cleanPrimaryComponentName = (name, subjectName) => {
    let label = String(name || "Assessment").trim();
    const subject = String(subjectName || "").trim();

    if (subject) {
      label = label.replace(new RegExp(`\\s*\\(${escapeRegExp(subject)}\\)\\s*$`, "i"), "");
    }

    return label.replace(/\s{2,}/g, " ").trim() || "Assessment";
  };

  const getPrimarySubjectComponents = (student, subjectName) => {
    const compMap = new Map();

    (student?.components || [])
      .filter((c) => c.subject_name === subjectName)
      .forEach((c) => {
        const key = Number(c.component_id) || getPrimaryComponentName(c);
        if (!compMap.has(key)) {
          compMap.set(key, {
            component_id: c.component_id,
            name: getPrimaryComponentDisplayName(c, subjectName),
          });
        }
      });

    return Array.from(compMap.values());
  };

  const buildPrimarySubjectTableHtml = (student, subjectName) => {
    const components = getPrimarySubjectComponents(student, subjectName);
    const s1 = term1Id ? getSubjectTermStats(student, subjectName, term1Id) : null;
    const s2 = term2Id ? getSubjectTermStats(student, subjectName, term2Id) : null;

    return `
      <table class="primary-subject-table">
        <thead>
          <tr>
            <th colspan="3" class="primary-subject-title">${escapePrimaryHtml(subjectName)}</th>
          </tr>
          <tr>
            <th class="skill-name-col">Assessment / Component</th>
            <th>${term1Id ? termLabel(term1Id) : "Term-I"}</th>
            <th>${term2Id ? termLabel(term2Id) : "Term-II"}</th>
          </tr>
        </thead>
        <tbody>
          ${
            components.length
              ? components
                  .map((component) => {
                    const t1 = term1Id
                      ? getSubjectTermCompDisplay(
                          student,
                          subjectName,
                          term1Id,
                          component.component_id
                        )
                      : "-";
                    const t2 = term2Id
                      ? getSubjectTermCompDisplay(
                          student,
                          subjectName,
                          term2Id,
                          component.component_id
                        )
                      : "-";

                    return `
                      <tr>
                        <td class="skill-name">${escapePrimaryHtml(component.name)}</td>
                        <td>${escapePrimaryHtml(t1)}</td>
                        <td>${escapePrimaryHtml(t2)}</td>
                      </tr>
                    `;
                  })
                  .join("")
              : `<tr><td colspan="3">No components available</td></tr>`
          }
          <tr class="primary-overall-row">
            <td>Overall Grade</td>
            <td>${escapePrimaryHtml(s1?.grade || "-")}</td>
            <td>${escapePrimaryHtml(s2?.grade || "-")}</td>
          </tr>
        </tbody>
      </table>
    `;
  };

  const buildPrimaryCoScholasticRowsHtml = (student) => {
    const studentId = student?.id;
    const t1 = term1Id ? coScholasticByTerm[String(term1Id)] || {} : {};
    const t2 = term2Id ? coScholasticByTerm[String(term2Id)] || {} : {};

    const s1 = Object.values(t1[String(studentId)] || {});
    const s2 = Object.values(t2[String(studentId)] || {});

    const map = new Map();
    s1.forEach((g) => map.set(g.area_id, { area_name: g.area_name, t1: g, t2: null }));
    s2.forEach((g) => {
      const prev = map.get(g.area_id);
      if (prev) map.set(g.area_id, { ...prev, t2: g });
      else map.set(g.area_id, { area_name: g.area_name, t1: null, t2: g });
    });

    mergeCoScholasticSubjectRows(map, student);

    const rows = Array.from(map.values());

    return rows.length
      ? rows
          .map(
            (r) => `
              <tr>
                <td class="skill-name">${escapePrimaryHtml(r.area_name || "-")}</td>
                <td>${escapePrimaryHtml(r.t1?.grade || "-")}</td>
                <td>${escapePrimaryHtml(r.t2?.grade || "-")}</td>
              </tr>
            `
          )
          .join("")
      : `<tr><td colspan="3">No co-scholastic data available</td></tr>`;
  };

  const buildPrimarySectionCardsHtml = (studentsForPdf = reportData || [], infoMapOverride = studentInfoMap, formatAssets = {}) => {
    const styles = `
      <style>
        * { box-sizing: border-box; }
        @page { size: A4 landscape; margin: 5mm; }
        body {
          margin: 0;
          padding: 0;
          font-family: Arial, Helvetica, sans-serif;
          font-size: 8.4px;
          color: #111827;
          background: #fff;
        }
        section.primary-card {
          page-break-after: always;
          page-break-inside: avoid;
          break-inside: avoid;
          border: 2px solid #111;
          padding: 5px;
          height: 200mm;
          max-height: 200mm;
          overflow: hidden;
        }
        section.primary-card:last-child { page-break-after: auto; }
        .primary-header {
          display: grid;
          grid-template-columns: 70px 1fr 58px 62px;
          gap: 5px;
          align-items: center;
          border-bottom: 2px solid #111;
          padding-bottom: 4px;
          margin-bottom: 4px;
        }
        .primary-logo {
          width: 62px;
          height: 62px;
          object-fit: contain;
        }
        .primary-board-logo {
          width: 50px;
          height: 50px;
          object-fit: contain;
          justify-self: center;
        }
        .primary-photo {
          width: 58px;
          height: 68px;
          object-fit: cover;
          border: 1px solid #111;
          justify-self: end;
        }
        .primary-school {
          text-align: center;
          line-height: 1.22;
          font-size: 9.4px;
        }
        .primary-school h1 {
          margin: 0 0 3px 0;
          font-size: 15px;
          text-transform: uppercase;
        }
        .primary-title {
          display: inline-block;
          margin-top: 4px;
          padding: 2px 14px;
          border: 1px solid #111;
          font-weight: 800;
          text-transform: uppercase;
          font-size: 9.4px;
        }
        .student-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          border-top: 1px solid #111;
          border-left: 1px solid #111;
          margin-bottom: 4px;
        }
        .student-cell {
          border-right: 1px solid #111;
          border-bottom: 1px solid #111;
          padding: 2px 4px;
          min-height: 16px;
          line-height: 1.08;
        }
        .label { font-weight: 800; }
        .subject-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 4px;
          align-items: stretch;
        }
        .primary-subject-table {
          width: 100%;
          border-collapse: collapse;
          page-break-inside: avoid;
          break-inside: avoid;
          margin-bottom: 3px;
          height: 100%;
        }
        .primary-subject-table th,
        .primary-subject-table td {
          border: 1px solid #111;
          padding: 2px 3px;
          text-align: center;
          vertical-align: middle;
          line-height: 1.05;
        }
        .primary-subject-table th {
          background: #e5e7eb;
          font-weight: 800;
        }
        .primary-subject-title {
          background: #cbd5e1 !important;
          text-transform: uppercase;
          font-size: 8.2px;
          letter-spacing: .15px;
        }
        .skill-name-col { width: 72%; }
        .skill-name {
          text-align: left !important;
          font-weight: 600;
          white-space: normal;
          word-break: normal;
        }
        .primary-overall-row td {
          background: #f3f4f6;
          font-weight: 800;
        }
        .bottom-grid {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 4px;
          margin-top: 3px;
        }
        .small-box {
          border: 1px solid #111;
          min-height: 34px;
        }
        .box-title {
          background: #e5e7eb;
          border-bottom: 1px solid #111;
          text-align: center;
          font-weight: 800;
          padding: 2px 4px;
          text-transform: uppercase;
        }
        .box-body { padding: 3px 4px; line-height: 1.15; }
        .sign-row {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          margin-top: 7px;
          text-align: center;
          font-weight: 800;
        }
        .signature-space {
          height: 20px;
          border-bottom: 1px solid #111;
          margin-bottom: 3px;
        }
        .sign-line {
          padding-top: 0;
          line-height: 1.05;
        }
        .grade-key {
          margin-top: 4px;
          border: 1px solid #111;
          display: grid;
          grid-template-columns: 1fr 1fr 1fr 1.6fr;
        }
        .grade-key div {
          border-right: 1px solid #111;
          padding: 2px 4px;
          text-align: center;
        }
        .grade-key div:last-child { border-right: 0; }
      </style>
    `;

    const cards = (studentsForPdf || []).map((student) => {
      const info = infoMapOverride[student.id] || studentInfoMap[student.id] || {};
      const mergedInfoForPhoto = { ...student, ...info };
      const studentPhotoSrc = info?.__pdfPhotoSrc || getStudentPhotoURL(mergedInfoForPhoto);
      const allSubjects = getAllSubjectsForPrimary(student);

      const effectiveHeaderHtml = getReportCardHeaderHtml();
      const effectiveSchoolLogoUrl = getReportCardSchoolLogoUrl(formatAssets);
      const effectiveBoardLogoUrl = getReportCardBoardLogoUrl(formatAssets);
      const cleanHeaderHtml = sanitizeHeaderHtml(effectiveHeaderHtml);

      const dobValRaw = info?.Date_Of_Birth || info?.date_of_birth || info?.dob || "";
      const dobVal = formatDOB(dobValRaw);

      const a1 = term1Id ? attendanceByTerm[String(term1Id)]?.[student.id] : null;
      const a2 = term2Id ? attendanceByTerm[String(term2Id)]?.[student.id] : null;
      const r2 = term2Id ? remarksByTerm[String(term2Id)]?.[student.id] : null;

      return `
        <section class="primary-card">
          <div class="primary-header">
            <div>
              ${
                effectiveSchoolLogoUrl
                  ? `<img src="${escapePrimaryHtml(effectiveSchoolLogoUrl)}" class="primary-logo" />`
                  : `<div style="font-weight:800;text-align:center;">School<br/>Logo</div>`
              }
            </div>
            <div class="primary-school">
              ${
                cleanHeaderHtml
                  ? cleanHeaderHtml
                  : `<h1>School Name</h1><div>Primary Section</div><div class="primary-title">Report Card</div>`
              }
            </div>
            <div style="display:flex;justify-content:center;align-items:center;">
              ${
                effectiveBoardLogoUrl
                  ? `<img src="${escapePrimaryHtml(effectiveBoardLogoUrl)}" alt="Board Logo" class="primary-board-logo" />`
                  : `<span style="display:block;width:50px;height:50px;"></span>`
              }
            </div>
            <img src="${escapePrimaryHtml(studentPhotoSrc)}" alt="Student Photo" class="primary-photo" onerror="this.onerror=null;this.src='${NO_PHOTO_SVG}';" />
          </div>

          <div class="student-grid">
            <div class="student-cell"><span class="label">Student Name:</span> ${escapePrimaryHtml(info?.name)}</div>
            <div class="student-cell"><span class="label">Admission No.:</span> ${escapePrimaryHtml(info?.admission_number)}</div>
            <div class="student-cell"><span class="label">Class / Section:</span> ${escapePrimaryHtml(info?.Class?.class_name)} - ${escapePrimaryHtml(info?.Section?.section_name)}</div>
            <div class="student-cell"><span class="label">Date of Birth:</span> ${escapePrimaryHtml(dobVal)}</div>
            <div class="student-cell"><span class="label">Mother's Name:</span> ${escapePrimaryHtml(info?.mother_name)}</div>
            <div class="student-cell"><span class="label">Father's Name:</span> ${escapePrimaryHtml(info?.father_name)}</div>
            <div class="student-cell"><span class="label">Session:</span> ${escapePrimaryHtml(sessions.find((x) => String(x.id) === String(filters.session_id))?.name || "-")}</div>
            <div class="student-cell"><span class="label">Roll No.:</span> ${escapePrimaryHtml(info?.roll_number)}</div>
            <div class="student-cell"><span class="label">Class Teacher:</span> ${escapePrimaryHtml(info?.class_teacher_name || "-")}</div>
          </div>

          <div class="subject-grid">
            ${
              allSubjects.length
                ? allSubjects.map((subjectName) => buildPrimarySubjectTableHtml(student, subjectName)).join("")
                : `<table class="primary-subject-table"><tr><td>No scholastic data available</td></tr></table>`
            }
          </div>

          <div class="bottom-grid">
            <div class="small-box">
              <div class="box-title">Attendance</div>
              <div class="box-body">
                <div><b>${term1Id ? termLabel(term1Id) : "Term-I"}:</b> ${escapePrimaryHtml(buildPresentTotalText(a1))}</div>
                <div><b>${term2Id ? termLabel(term2Id) : "Term-II"}:</b> ${escapePrimaryHtml(buildPresentTotalText(a2))}</div>
              </div>
            </div>
            <div class="small-box">
              <div class="box-title">Health Details</div>
              <div class="box-body">
                <div><b>Height:</b> ${escapePrimaryHtml(info?.height || "-")} &nbsp; <b>Weight:</b> ${escapePrimaryHtml(info?.weight || "-")}</div>
                <div><b>Dental:</b> ${escapePrimaryHtml(info?.dental || "-")} &nbsp; <b>Vision:</b> ${escapePrimaryHtml(info?.vision || "-")} &nbsp; <b>Blood Group:</b> ${escapePrimaryHtml(info?.blood_group || "-")}</div>
              </div>
            </div>
            <div class="small-box">
              <div class="box-title">Class Teacher's Remark</div>
              <div class="box-body">${escapePrimaryHtml(r2 || "-")}</div>
            </div>
          </div>

          <div class="sign-row">
            <div class="sign-line"><div class="signature-space"></div>Class Teacher's Sign.</div>
            <div class="sign-line"><div class="signature-space"></div>Headmistress Sign.</div>
            <div class="sign-line"><div class="signature-space"></div>Principal's Sign.</div>
          </div>

          <div class="grade-key">
            <div><b>G</b> Excellent</div>
            <div><b>B</b> Very Good</div>
            <div><b>Y</b> Good</div>
            <div><b>R</b> Is learning with guidance but still needs encouragement</div>
          </div>
        </section>
      `;
    });

    return `<!doctype html><html><head><meta charset="utf-8" />${styles}</head><body>${cards.join("")}</body></html>`;
  };

  const buildCardsHtml = (studentsForPdf = reportData || [], infoMapOverride = studentInfoMap, formatAssets = {}) => {
    if (isPrimarySectionTemplateActive()) {
      return buildPrimarySectionCardsHtml(studentsForPdf, infoMapOverride, formatAssets);
    }

    const styles = `
    <style>
      * { box-sizing: border-box; }

      body {
        font-family: "Helvetica", Arial, sans-serif;
        font-size: 13px;
        color: #0f172a;
        background:
          radial-gradient(1100px 600px at 12% 0%, rgba(59,130,246,0.18), transparent 60%),
          radial-gradient(1000px 520px at 88% 8%, rgba(16,185,129,0.16), transparent 60%),
          radial-gradient(900px 520px at 50% 90%, rgba(168,85,247,0.10), transparent 60%),
          linear-gradient(180deg, #eef5ff 0%, #fbfdff 100%);
      }

      @page { margin: 4px 4px; }

      section { page-break-after: always; }
      section:last-child { page-break-after: auto; }

      .card { margin-bottom: 0px; }

      .panel {
        border: 1px solid rgba(199,210,254,0.85);
        border-radius: 8px;
        padding: 5px;
        background: linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(248,251,255,0.96) 100%);
        box-shadow: none;
        position: relative;
        overflow: hidden;
      }

      .header-flex {
        display: grid;
        grid-template-columns: 96px 1fr 96px;
        align-items: center;
        gap: 6px;
      }

      .header-left {
        display: flex;
        justify-content: flex-start;
        align-items: flex-start;
        padding-top: 4px;
        padding-left: 4px;
      }

      .header-center {
        text-align: center;
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 92px;
        padding: 0 5px;
        font-size: 12px;
        line-height: 1.08;
        color: #0b1b3a;
      }

      .header-center .rc-header {
        text-align: center;
        padding: 4px 5px 3px;
        line-height: 1.08;
        background: transparent;
      }

      .header-center .school-address {
        margin: 2px 0 0 !important;
      }

      .header-center .rc-session {
        margin-top: 3px !important;
        font-size: 13px !important;
        line-height: 1.1 !important;
        letter-spacing: 0.6px !important;
      }

      .header-center .rc-title {
        margin-top: 2px !important;
      }

      .header-right {
        display: flex;
        justify-content: flex-end;
        align-items: flex-start;
        gap: 4px;
      }

      .header-logo {
        height: 74px;
        width: auto;
        object-fit: contain;
        display: block;
        flex-shrink: 0;
        margin-left: 4px;
      }

      .header-board-logo {
        height: 54px;
        width: auto;
        max-width: 60px;
        object-fit: contain;
        display: block;
        background: transparent;
        flex-shrink: 0;
        margin-top: 4px;
      }

      .student-photo {
        width: 74px;
        height: 88px;
        border-radius: 7px;
        object-fit: cover;
        border: 1px solid rgba(191,219,254,1);
        box-shadow: none;
        background:#fff;
      }

      .grid-info{
        display:grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 3px 4px;
        font-size: 9.5px;
        margin-top: 0;
      }

      .kv{
        padding: 3px 4px;
        border-radius: 5px;
        background: rgba(255,255,255,0.8);
        border:1px solid rgba(226,232,240,0.9);
        line-height: 1.12;
      }

      .kv b{ color:#0b1b3a; }

      .section-title{
        display:flex;
        align-items:center;
        justify-content:space-between;
        margin-top: 3px;
        margin-bottom: 2px;
      }

      .section-title-left{
        display:flex;
        align-items:center;
        gap:5px;
      }

      .section-pill{
        font-size: 7px;
        font-weight: 900;
        padding: 2px 5px;
        border-radius: 999px;
        background: rgba(59,130,246,0.12);
        border: 1px solid rgba(59,130,246,0.22);
        color:#0b1b3a;
        letter-spacing: 0.3px;
        text-transform: uppercase;
      }

      .tbl {
        width: 100%;
        border-collapse: collapse;
        background: rgba(255,255,255,0.95);
      }

      .tbl th,
      .tbl td {
        border: 1px solid rgba(148,163,184,0.9);
        padding: 3px 4px;
        text-align: center;
        vertical-align: middle;
        line-height: 1.08;
        font-size: 7.6px;
        white-space: nowrap;
      }

      .tbl td:first-child,
      .tbl th:first-child {
        text-align: left !important;
        white-space: normal !important;
      }

      .tbl thead th{
        background: linear-gradient(180deg,#1e3a8a,#1e40af);
        color: #ffffff;
        font-weight: 800;
      }

      .tbl tbody td{
        background: #fff7cc;
        color: #0f172a;
      }

      .tbl tbody tr:nth-child(even) td{
        background: #fff2a8;
        color: #0f172a;
      }

      .th-subject{
        background: linear-gradient(180deg,#1e3a8a,#1e40af);
        color:#ffffff;
        text-align:left;
        font-size: 7.8px;
      }

      .th-term{
        background: linear-gradient(180deg,#1e3a8a,#1e40af);
        color:#ffffff;
        font-size: 7.8px;
      }

      .th-grand{
        background: linear-gradient(180deg,#172554,#1e3a8a);
        color:#ffffff;
        font-size: 7.8px;
      }

      .th-comp{
        background: linear-gradient(180deg,#1e40af,#2563eb);
        color:#ffffff;
        font-weight:700;
        font-size:8px;
      }

      .th-comp.strong{
        font-weight:900;
      }

      .td-subject{
        background: #ffe68a !important;
        color: #0b1b3a !important;
        font-weight: 900;
        text-align:left;
        white-space: normal;
        font-size: 7.8px;
      }

      .td-strong{
        font-weight: 900;
        color:#0b1b3a !important;
      }

      .td-total-label,
      .td-rank-label{
        background: linear-gradient(180deg,#1e3a8a,#1e40af);
        font-weight: 900;
        text-align:left;
        color:#ffffff !important;
        font-size: 7.8px;
      }

      .td-total{
        background:#ffe066 !important;
        font-weight:900;
        color:#0b1b3a !important;
      }

      .td-grand{
        background:#ffd43b !important;
        font-weight:900;
        color:#0b1b3a !important;
      }

      .td-rank-value{
        background: #fff7cc !important;
        font-weight: 900;
        text-align: right !important;
        padding-right: 6px !important;
        color:#0b1b3a !important;
        font-size: 7.8px;
      }

      .grand-total-small{
        font-weight: 900;
        font-size: 8px;
        letter-spacing: 0.1px;
        color:#0b1b3a !important;
      }

      .grand-grade-big{
        font-weight: 900;
        font-size: 7.8px;
        color:#0b1b3a !important;
      }

      .grand-percent-highlight{
        margin-top: 1px;
        font-size: 8px;
        font-weight: 900;
        display: inline-block;
        padding: 1px 4px;
        border-radius: 5px;
        background: rgba(255, 243, 199, 0.95);
        border: 1px solid rgba(251, 191, 36, 0.55);
        color: #0b1b3a !important;
      }

      .rank-highlight{
        display: inline-block;
        padding: 1px 4px;
        border-radius: 5px;
        background: rgba(255, 243, 199, 0.95);
        border: 1px solid rgba(251, 191, 36, 0.55);
        color: #0b1b3a !important;
        font-size: 8px;
        font-weight: 900;
        line-height: 1.1;
      }

      .muted{
        color:#475569;
      }

      .remarks-card{
        border:1px solid rgba(226,232,240,0.9);
        border-radius: 7px;
        background: rgba(255,255,255,0.86);
        overflow:hidden;
      }

      .remarks-body{
        padding: 4px 5px;
        min-height: 18px;
        line-height: 1.15;
        font-size: 9px;
        color:#0f172a;
      }

      .two-col{
        display:grid;
        grid-template-columns: 1fr;
        gap: 4px;
        margin-top: 3px;
      }

      .grade-footer-note{
        margin-top: 3px;
        margin-bottom: 3px;
        font-size: 8px;
        color: #334155;
        border-top: 1px dashed rgba(148,163,184,0.7);
        padding-top: 3px;
        line-height: 1.08;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    </style>
  `;

    const gradeFooterText = buildGradeRangeFooterText(gradeSchema);

    const blocks = (studentsForPdf || []).map((student) => {
      const info = infoMapOverride[student.id] || studentInfoMap[student.id] || {};
      const mergedInfoForPhoto = { ...student, ...info };
      const studentPhotoSrc = info?.__pdfPhotoSrc || getStudentPhotoURL(mergedInfoForPhoto);

      const subjectsForStudent = getNonDrawingSubjects(student);
      const regularSubjectsForStudent = subjectsForStudent.filter(
        (subjectName) => getSubjectComponentCount(student, subjectName) > 2
      );
      const compactSubjectsForStudent = subjectsForStudent.filter(
        (subjectName) => getSubjectComponentCount(student, subjectName) <= 2
      );

      const scholasticTable = `
      <div class="section-title">
        <div class="section-title-left">
          <div class="section-pill">Scholastic</div>
          <h5 style="margin:0;color:#0b1b3a;font-size:10px">Scholastic Areas (Term-wise)</h5>
        </div>
      </div>
      ${
        regularSubjectsForStudent.length
          ? `<table class="tbl">
              <thead>${buildScholasticHeaderHtml_TermWise()}</thead>
              <tbody>
                ${regularSubjectsForStudent.map((sub) => buildScholasticBodyRowHtml_TermWise(student, sub)).join("")}
                ${buildTotalsFooterRowHtml(student)}
              </tbody>
            </table>`
          : ""
      }
      ${buildCompactScholasticPdfHtml(student, compactSubjectsForStudent)}
    `;

      const attendanceTable = buildAttendancePdfHtml_TermWise(student.id);
      const remarksBlock = buildTeacherRemarksPdfHtml_TermWise(student.id);
      const coScholasticBlock = buildCoScholasticPdfHtml_TwoTerms(student);

      const effectiveHeaderHtml = getReportCardHeaderHtml();
      const effectiveFooterHtml = getReportCardFooterHtml();
      const effectiveSchoolLogoUrl = getReportCardSchoolLogoUrl(formatAssets);
      const effectiveBoardLogoUrl = getReportCardBoardLogoUrl(formatAssets);

      const cleanHeaderHtml = sanitizeHeaderHtml(effectiveHeaderHtml);

      const headerHtml = cleanHeaderHtml
        ? `
        <div style="margin-bottom:8px">
          <div class="header-flex">
            <div class="header-left">
              ${
                effectiveSchoolLogoUrl
                  ? `<img src="${effectiveSchoolLogoUrl}" alt="School Logo" class="header-logo" />`
                  : `<span style="width:95px;display:block;"></span>`
              }
            </div>

            <div class="header-center">
              <div>${cleanHeaderHtml}</div>
            </div>

            <div class="header-right">
              ${
                effectiveBoardLogoUrl
                  ? `<img src="${escapePrimaryHtml(effectiveBoardLogoUrl)}" alt="Board Logo" class="header-board-logo" />`
                  : ""
              }
              <img src="${escapePrimaryHtml(studentPhotoSrc)}" alt="Student Photo" class="student-photo" onerror="this.onerror=null;this.src='${NO_PHOTO_SVG}';" />
            </div>
          </div>
        </div>
      `
        : "";

      const footerHtml = effectiveFooterHtml
        ? `<div style="margin-top:6px;text-align:center;font-size:11px;color:#334155">${effectiveFooterHtml}</div>`
        : "";

      const dobValRaw = info?.Date_Of_Birth || info?.date_of_birth || info?.dob || "";
      const dobVal = formatDOB(dobValRaw);
      const fatherVal = info?.father_name || "-";
      const motherVal = info?.mother_name || "-";

      const studentInfoBlock = `
      <div class="panel" style="margin-bottom:8px">
        <div class="grid-info">
          <div class="kv"><b>Student Name:</b> ${info?.name || "-"}</div>
          <div class="kv"><b>Admission No.:</b> ${info?.admission_number || "-"}</div>
          <div class="kv"><b>Class / Section:</b> ${(info?.Class?.class_name || "-")} - ${(info?.Section?.section_name || "-")}</div>

          <div class="kv"><b>Date of Birth:</b> ${dobVal}</div>
          <div class="kv"><b>Mother's Name:</b> ${motherVal}</div>
          <div class="kv"><b>Father's Name:</b> ${fatherVal}</div>
        </div>
      </div>
    `;

      return `
      <section class="card">
        ${headerHtml}
        ${studentInfoBlock}
        ${scholasticTable}

        ${
          gradeFooterText
            ? `<div class="grade-footer-note"><b>Grade Scale:</b> ${gradeFooterText}</div>`
            : ""
        }

        ${coScholasticBlock}

        <div class="two-col">
          <div>${attendanceTable}</div>
        </div>

        ${remarksBlock}
        ${footerHtml}
      </section>
    `;
    });

    return `<!doctype html><html><head><meta charset="utf-8" />${styles}</head><body>${blocks.join(
      ""
    )}</body></html>`;
  };

  const openAndDownloadPdfBlob = (blob, fileNameWithPdf) => {
    const url = window.URL.createObjectURL(blob);

    const newTab = window.open(url, "_blank");
    if (!newTab) console.warn("Popup blocked, downloading instead.");

    const a = document.createElement("a");
    a.href = url;
    a.download = fileNameWithPdf;
    document.body.appendChild(a);
    a.click();
    a.remove();

    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  };

  const downloadPDF = async () => {
    if (!reportData.length) {
      return Swal.fire("No Data", "Please generate the report first", "info");
    }

    const selected = getStudentsForPdf();

    if (!selected.length) {
      return Swal.fire(
        "No Students Selected",
        pdfMode === "range"
          ? "Range is invalid or roll numbers not found."
          : "Please select a student.",
        "warning"
      );
    }

    if (!selectedTemplateId) {
      return Swal.fire(
        "Select Template",
        "Please select a report card template before generating PDF.",
        "warning"
      );
    }

    const activeReportFormat = selectedReportFormat || reportFormatRef.current || reportFormat;

    if (!isSmartReportTemplate && !activeReportFormat?.id) {
      return Swal.fire(
        "Select Format",
        "Please select the report card print format before generating PDF.",
        "warning"
      );
    }

    const suffix =
      pdfMode === "single"
        ? `Student_${pdfSingleId || "X"}`
        : pdfMode === "range"
        ? `Roll_${pdfFrom || "X"}-${pdfTo || "X"}`
        : "All";

    const fileName = `FinalResult_TermWise_${filters.session_id || "X"}_${filters.class_id || "X"}_${filters.section_id || "X"}_${suffix}.pdf`;

    setPdfPercent(1);
    setPdfMessage("Preparing student photos…");
    setPdfProgressVisible(true);

    const controller = new AbortController();
    abortGenRef.current = controller;

    try {
      let res;

      if (isSmartReportTemplate) {
        setPdfMessage("Preparing smart template data & student photos…");
        const pdfInfoMap = await prepareStudentPhotoDataUrlsForPdf(selected);
        const records = selected.map((student) => ({
          student_id: student.id,
          data: buildSmartTemplateData(student, pdfInfoMap[student.id] || studentInfoMap[student.id] || {}),
        }));

        setPdfPercent(5);
        setPdfMessage("Overlaying ERP data on school design…");
        res = await api.post(
          `/report-card/templates/${selectedTemplateId}/render-pdf`,
          { records, fileName },
          {
            responseType: "blob",
            signal: controller.signal,
            onDownloadProgress: (e) => {
              if (e.total) {
                const pct = Math.min(98, Math.round((e.loaded / e.total) * 100));
                setPdfPercent(pct);
                setPdfMessage(pct < 90 ? "Rendering smart report cards…" : "Finalizing…");
              } else {
                setPdfPercent((p) => (p < 80 ? p + 1 : p));
                setPdfMessage("Rendering smart report cards…");
              }
            },
          }
        );
      } else {
        setPdfMessage("Preparing selected report format…");
        setReportFormat(activeReportFormat);

        const [pdfInfoMap, pdfFormatAssets] = await Promise.all([
          prepareStudentPhotoDataUrlsForPdf(selected),
          prepareReportFormatAssetsForPdf(),
        ]);
        const html = buildCardsHtml(selected, pdfInfoMap, pdfFormatAssets);

        setPdfPercent(2);
        setPdfMessage("Queuing render…");

        res = await api.post(
          PDF_ENDPOINT,
          {
            html,
            format_id: activeReportFormat.id,
            fileName,
            orientation: isPrimarySectionTemplateActive() ? "landscape" : selectedReportTemplate?.orientation || "portrait",
            session_id: Number(filters.session_id),
            class_id: Number(filters.class_id),
            template_id: Number(selectedTemplateId),
            template_key: selectedReportTemplate?.template_key || null,
            selected_template: selectedReportTemplate || null,
            school_logo_url: getReportCardFormatValue("school_logo_url") || null,
            asset_base_url: assetBase,
          },
          {
            responseType: "blob",
            signal: controller.signal,
            onDownloadProgress: (e) => {
              if (e.total) {
                const pct = Math.min(98, Math.round((e.loaded / e.total) * 100));
                setPdfPercent(pct);
                setPdfMessage(pct < 90 ? "Rendering PDF…" : "Finalizing…");
              } else {
                setPdfPercent((p) => (p < 80 ? p + 1 : p));
                setPdfMessage("Rendering PDF…");
              }
            },
          }
        );
      }

      setPdfPercent(100);
      setPdfMessage("Opening…");

      const blob = new Blob([res.data], { type: "application/pdf" });
      openAndDownloadPdfBlob(blob, fileName);
    } catch (error) {
      if (controller.signal.aborted) {
        Swal.fire("Cancelled", "PDF generation was cancelled.", "info");
      } else {
        console.error(error);
        Swal.fire("Error", "Failed to generate PDF", "error");
      }
    } finally {
      setPdfProgressVisible(false);
      abortGenRef.current = null;
    }
  };

  const renderCoScholasticTwoTermsTable = (student) => {
    const studentId = student?.id;
    const t1 = term1Id ? coScholasticByTerm[String(term1Id)] || {} : {};
    const t2 = term2Id ? coScholasticByTerm[String(term2Id)] || {} : {};

    const s1 = Object.values(t1[String(studentId)] || {});
    const s2 = Object.values(t2[String(studentId)] || {});

    const areasMap = new Map();
    s1.forEach((g) =>
      areasMap.set(g.area_id, { area_name: g.area_name, t1: g, t2: null })
    );

    s2.forEach((g) => {
      const prev = areasMap.get(g.area_id);
      if (prev) areasMap.set(g.area_id, { ...prev, t2: g });
      else areasMap.set(g.area_id, { area_name: g.area_name, t1: null, t2: g });
    });

    mergeCoScholasticSubjectRows(areasMap, student);

    const drawingAlreadyHandled = getCoScholasticSubjectNames(student).some(isDrawingSubject);
    const drawingT1 = getDrawingGradeForTerm(student, term1Id, exams, gradeSchema);
    const drawingT2 = getDrawingGradeForTerm(student, term2Id, exams, gradeSchema);

    if (!drawingAlreadyHandled && (drawingT1 !== "-" || drawingT2 !== "-")) {
      areasMap.set("__drawing__", {
        area_name: "Drawing",
        t1: { grade: drawingT1 },
        t2: { grade: drawingT2 },
      });
    }

    const rows = Array.from(areasMap.values());

    return (
      <div className="table-responsive">
        <table className="table table-bordered text-center small">
          <thead>
            <tr>
              <th
                style={{
                  background: "linear-gradient(180deg,var(--edb-primary-soft),var(--edb-primary-soft))",
                  color: "var(--edb-primary-dark-text)",
                  textAlign: "left",
                  fontSize: "14px",
                }}
              >
                Area
              </th>
              <th
                style={{
                  background: "linear-gradient(180deg,var(--edb-primary-soft),var(--edb-primary-soft))",
                  color: "var(--edb-primary-dark-text)",
                  fontSize: "14px",
                }}
              >
                {term1Id ? termLabel(term1Id) : "Term-I"} Grade
              </th>
              <th
                style={{
                  background: "linear-gradient(180deg,var(--edb-primary-soft),var(--edb-primary-soft))",
                  color: "var(--edb-primary-dark-text)",
                  fontSize: "14px",
                }}
              >
                {term2Id ? termLabel(term2Id) : "Term-II"} Grade
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={idx}>
                <td style={{ textAlign: "left", fontWeight: "bold", fontSize: "14px" }}>
                  {r.area_name || "-"}
                </td>
                <td style={{ fontSize: "14px" }}>{r.t1?.grade || "-"}</td>
                <td style={{ fontSize: "14px" }}>{r.t2?.grade || "-"}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={3} className="text-center">
                  No co-scholastic data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const renderAttendanceTermWise = (studentId) => {
    const a1 = term1Id ? attendanceByTerm[String(term1Id)]?.[studentId] : null;
    const a2 = term2Id ? attendanceByTerm[String(term2Id)]?.[studentId] : null;

    const t1Text = buildPresentTotalText(a1);
    const t2Text = buildPresentTotalText(a2);

    const t1Pct = buildAttendancePercent(a1);
    const t2Pct = buildAttendancePercent(a2);

    return (
      <div className="table-responsive">
        <table className="table table-bordered text-center small">
          <thead>
            <tr>
              <th
                style={{
                  background: "linear-gradient(180deg,var(--edb-primary-soft),var(--edb-primary-soft))",
                  color: "var(--edb-primary-dark-text)",
                  fontSize: "14px",
                }}
              >
                {term1Id ? termLabel(term1Id) : "Term-I"}
              </th>
              <th
                style={{
                  background: "linear-gradient(180deg,var(--edb-primary-soft),var(--edb-primary-soft))",
                  color: "var(--edb-primary-dark-text)",
                  fontSize: "14px",
                }}
              >
                {term2Id ? termLabel(term2Id) : "Term-II"}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ fontWeight: 900, fontSize: "14px" }}>
                <div>{t1Text}</div>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  {t1Pct != null ? `${formatNumber(t1Pct)}%` : "-"}
                </div>
              </td>
              <td style={{ fontWeight: 900, fontSize: "14px" }}>
                <div>{t2Text}</div>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  {t2Pct != null ? `${formatNumber(t2Pct)}%` : "-"}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

const renderTeacherRemarksTermWise = (studentId) => {
  const r2 = term2Id ? remarksByTerm[String(term2Id)]?.[studentId] : null;
  const promotion = term2Id
    ? promotionDecisionByTerm[String(term2Id)]?.[studentId]
    : null;

  const showPromotionFields =
    promotion &&
    promotion.promotion_status === "PROMOTED" &&
    promotion.promoted_to_class_id &&
    Number(promotion.promoted_to_class_id) !== Number(promotion.current_class_id);

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr",
        gap: "8px",
      }}
    >
      <div className="panel small">
     
        <div style={{ fontSize: "14px", lineHeight: 1.45 }}>
          {(r2 || "-").trim() || "-"}
        </div>
      </div>

      {showPromotionFields && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "8px",
          }}
        >
          <div className="panel small">
            <div
              style={{
                fontSize: "13px",
                lineHeight: 1.45,
                color: "var(--edb-primary-dark-text)",
              }}
            >
              <span style={{ fontWeight: 800, color: "var(--edb-text)" }}>
                Promoted To Class:
              </span>{" "}
              <span style={{ fontWeight: 700 }}>
                {promotion.promoted_to_class_name || "-"}
              </span>
            </div>
          </div>

          <div className="panel small">
            <div
              style={{
                fontSize: "13px",
                lineHeight: 1.45,
                color: "var(--edb-primary-dark-text)",
              }}
            >
              <span style={{ fontWeight: 800, color: "var(--edb-text)" }}>
                Promotion Date:
              </span>{" "}
              <span style={{ fontWeight: 700 }}>
                {promotion.promotion_date
                  ? formatDisplayDate(promotion.promotion_date)
                  : "-"}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

  const gradeFooterText = buildGradeRangeFooterText(gradeSchema);

  return (
    <div className="container mt-4">
      <style>{`
        body {
          background:
            radial-gradient(1200px 600px at 10% 0%, color-mix(in srgb, var(--edb-primary) 20%, transparent), transparent 60%),
            radial-gradient(1000px 500px at 90% 10%, rgba(16,185,129,0.18), transparent 60%),
            radial-gradient(900px 520px at 50% 92%, color-mix(in srgb, var(--edb-primary) 12%, transparent), transparent 60%),
            linear-gradient(180deg, var(--edb-surface) 0%, var(--edb-surface) 100%);
        }
        .page-bg {
          background:
            radial-gradient(1200px 600px at 10% 0%, color-mix(in srgb, var(--edb-primary) 18%, transparent), transparent 60%),
            radial-gradient(1000px 500px at 90% 10%, rgba(16,185,129,0.16), transparent 60%),
            radial-gradient(900px 520px at 50% 92%, color-mix(in srgb, var(--edb-primary) 10%, transparent), transparent 60%),
            linear-gradient(180deg, color-mix(in srgb, var(--edb-surface) 92%, transparent) 0%, color-mix(in srgb, var(--edb-surface) 95%, transparent) 100%);
          border-radius: 18px;
          padding: 16px;
          border: 1px solid color-mix(in srgb, var(--edb-border) 70%, transparent);
          box-shadow: 0 12px 26px color-mix(in srgb, var(--edb-primary-dark) 10%, transparent);
        }
        .report-card {
          background: linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(248,251,255,0.96) 100%);
          border-radius: 16px;
          border: 1px solid rgba(199,210,254,0.75);
          box-shadow: 0 12px 26px rgba(10, 30, 80, 0.12);
          position: relative;
          overflow: hidden;
        }
        .panel {
          background: color-mix(in srgb, var(--edb-surface) 92%, transparent);
          border: 1px solid color-mix(in srgb, var(--edb-border) 75%, transparent);
          border-radius: 16px;
          padding: 10px;
          box-shadow: 0 8px 18px color-mix(in srgb, var(--edb-primary-dark) 8%, transparent);
        }
        .report-card .table-responsive {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }
        .report-card .table>:not(caption)>*>*{
          padding: 0.4rem 0.46rem !important;
          line-height: 1.12;
        }
        .report-card th, .report-card td {
          white-space: nowrap;
          font-size: 14px;
          vertical-align: middle;
        }
        .report-card tbody tr:nth-child(odd) td { background: rgba(255,255,255,0.92); }
        .report-card tbody tr:nth-child(even) td { background: rgba(241,245,255,0.75); }
        .report-card td:first-child, .report-card th:first-child {
          white-space: normal;
          min-width: 180px;
        }
        .sticky-first-col {
          position: sticky;
          left: 0;
          z-index: 2;
          background: linear-gradient(180deg,var(--edb-primary-soft),var(--edb-primary-soft));
          text-align: left;
          font-size: 14px;
        }
        .sticky-first-col-td {
          position: sticky;
          left: 0;
          z-index: 1;
          background: color-mix(in srgb, var(--edb-primary-soft) 92%, transparent);
          text-align: left;
          font-size: 14px;
          font-weight: 900;
        }
        .section-pill {
          font-size: 10px;
          font-weight: 900;
          padding: 4px 10px;
          border-radius: 999px;
          background: color-mix(in srgb, var(--edb-primary) 12%, transparent);
          border: 1px solid color-mix(in srgb, var(--edb-primary) 22%, transparent);
          color:var(--edb-primary-dark-text);
          letter-spacing: 0.3px;
          text-transform: uppercase;
        }
        .grand-total-small {
          font-weight: 900;
          font-size: 9.4px;
          letter-spacing: 0.1px;
        }
        .grand-grade-big {
          font-weight: 900;
          font-size: 13px;
        }
        .grand-percent-highlight{
          margin-top: 2px;
          font-size: 9.4px;
          font-weight: 900;
          display: inline-block;
          padding: 2px 8px;
          border-radius: 10px;
          background: color-mix(in srgb, var(--edb-primary-soft) 95%, transparent);
          border: 1px solid color-mix(in srgb, var(--edb-primary) 55%, transparent);
          color: var(--edb-primary-dark-text);
        }
        .rank-highlight{
          display: inline-block;
          padding: 2px 8px;
          border-radius: 10px;
          background: color-mix(in srgb, var(--edb-primary-soft) 95%, transparent);
          border: 1px solid color-mix(in srgb, var(--edb-primary) 55%, transparent);
          color: var(--edb-primary-dark-text);
          font-size: 9.4px;
          font-weight: 900;
          line-height: 1.1;
        }
        .student-info-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 8px 10px;
        }
        .student-info-item {
          font-size: 14px;
          line-height: 1.3;
        }
        .rank-row-label {
          background: linear-gradient(180deg,var(--edb-primary-soft),var(--edb-primary-soft)) !important;
          color: var(--edb-primary-dark-text);
          font-weight: 900;
          text-align: left;
          font-size: 14px;
        }
        .rank-row-value {
          font-weight: 900;
          text-align: right;
          padding-right: 16px !important;
          background: color-mix(in srgb, var(--edb-surface) 92%, transparent);
          color:var(--edb-primary-dark-text);
          font-size: 14px;
        }
        .grade-footer-note{
          margin-top: 8px;
          margin-bottom: 8px;
          font-size: 13px;
          color: var(--edb-text);
          border-top: 1px dashed color-mix(in srgb, var(--edb-border) 70%, transparent);
          padding-top: 6px;
          line-height: 1.2;
          white-space: nowrap;
          overflow-x: auto;
        }
      `}</style>

      <div className="page-bg">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div>
            <h2 className="mb-0">📘 Final Result Summary (Term-I & Term-II)</h2>
            <div className="text-muted small mt-1">
              Professional print-ready report cards • compact layout • better single-page fit
            </div>
          </div>
          <div className="section-pill">Print Ready</div>
        </div>

        <div className="row g-3 mt-3">
          <div className="col-md-3">
            <label>Session</label>
            <select className="form-select" value={filters.session_id} onChange={handleSessionChange}>
              <option value="">Select Session</option>
              {sessions.map((session) => (
                <option key={session.id} value={session.id}>
                  {session.name}
                  {session.is_active ? " (Active)" : ""}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label>Class</label>
            <select className="form-select" value={filters.class_id} onChange={handleClassChange}>
              <option value="">Select Class</option>
              {classList.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.class_name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label>Section</label>
            <select
              name="section_id"
              className="form-select"
              value={filters.section_id}
              onChange={handleFilterChange}
            >
              <option value="">Select Section</option>
              {sections.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.section_name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label>Exam(s)</label>
            <select multiple className="form-select" value={filters.exam_ids} onChange={handleExamChange}>
              {exams.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </select>
            <div className="text-muted small mt-1">Hold Ctrl / Cmd • Select exams from both terms</div>
          </div>
        </div>

        <div className="mt-4">
          <h5 className="mb-2">Subjects & Components</h5>
          <div className="d-flex flex-wrap gap-3">
            {filters.subjectComponents.map((sc, i) => (
              <div key={i} className="panel" style={{ minWidth: 260 }}>
                <div className="d-flex gap-2">
                  <select
                    className="form-select mb-2"
                    value={sc.subject_id}
                    onChange={(e) => handleSubjectChange(e, i)}
                  >
                    <option value="">Subject</option>
                    {subjects.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>

                  <button className="btn btn-sm btn-danger mb-2" onClick={() => removeSubject(i)}>
                    Remove
                  </button>
                </div>

                {Object.entries(
                  (sc.availableComponents || []).reduce((a, c) => {
                    (a[c.term_id] || (a[c.term_id] = [])).push(c);
                    return a;
                  }, {})
                ).map(([term, comps]) => (
                  <div key={term} className="mb-2">
                    <small className="fw-bold">Term {term}</small>
                    <div className="d-flex flex-wrap">
                      {comps.map((c) => (
                        <div key={c.component_id} className="form-check me-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`c-${term}-${i}-${c.component_id}`}
                            checked={(sc.selected_components?.[String(term)] || []).includes(c.component_id)}
                            onChange={(e) =>
                              handleComponentToggle(+term, c.component_id, i, e.target.checked)
                            }
                          />
                          <label className="form-check-label" htmlFor={`c-${term}-${i}-${c.component_id}`}>
                            {c.name || c.component_name || c.componentName || c.abbreviation}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="mt-3">
            <button className="btn btn-success me-2" onClick={addSubject}>
              Add Subject
            </button>
            <button className="btn btn-primary" onClick={fetchReport} disabled={loading}>
              {loading ? "Loading…" : "Generate Report"}
            </button>
          </div>
        </div>

        <div className="form-check form-switch my-3">
          <input
            className="form-check-input"
            type="checkbox"
            id="toggleTotals"
            checked={showTotals}
            onChange={() => setShowTotals((prev) => !prev)}
          />
          <label className="form-check-label" htmlFor="toggleTotals">
            Show Total + Grade Columns
          </label>
        </div>

        {!loading && reportData.length > 0 && (
          <div className="mt-4">
            <div className="panel mb-3">
              <div className="row g-2 align-items-end">
                <div className="col-md-3">
                  <label className="form-label fw-bold">Print Mode</label>
                  <select
                    className="form-select"
                    value={pdfMode}
                    onChange={(e) => setPdfMode(e.target.value)}
                  >
                    <option value="all">All (Loaded Students)</option>
                    <option value="single">Single Student</option>
                    <option value="range">Range (Roll No.)</option>
                  </select>
                  <div className="text-muted small mt-1">Faster PDF for printing</div>
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-bold">Print Format</label>
                  <select
                    className="form-select"
                    value={selectedReportFormatId}
                    onChange={(e) => handleReportFormatChange(e.target.value)}
                    disabled={loadingReportFormats || !reportFormats.length || isSmartReportTemplate}
                  >
                    <option value="">
                      {loadingReportFormats ? "Loading formats…" : "Select Format"}
                    </option>
                    {reportFormats.map((format) => (
                      <option key={format.id} value={format.id}>
                        {format.title || format.format_key || `Format #${format.id}`}
                        {format.orientation ? ` (${format.orientation})` : ""}
                      </option>
                    ))}
                  </select>
                  <div className="text-muted small mt-1">{isSmartReportTemplate ? "Visual Designer template controls the complete PDF layout" : "Selected format controls PDF layout"}</div>
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-bold">Report Card Template</label>
                  <select
                    className="form-select"
                    value={selectedTemplateId}
                    onChange={(e) => setSelectedTemplateId(e.target.value)}
                    disabled={loadingTemplates || !filters.class_id}
                  >
                    <option value="">
                      {loadingTemplates ? "Loading templates…" : "Select Template"}
                    </option>
                    {reportTemplates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                        {template.is_default ? " (Default)" : ""}
                        {hasVisualReportCardLayout(template) ? " • Visual Designer" : ""}
                      </option>
                    ))}
                  </select>
                  <div className="text-muted small mt-1">{isSmartReportTemplate ? "Visual Designer layout will be rendered with live ERP data" : "Selected template will be sent to backend"}</div>
                </div>

                {pdfMode === "single" && (
                  <div className="col-md-5">
                    <label className="form-label fw-bold">Select Student</label>
                    <select
                      className="form-select"
                      value={pdfSingleId}
                      onChange={(e) => setPdfSingleId(e.target.value)}
                    >
                      <option value="">Select…</option>
                      {(reportData || []).map((s) => {
                        const info = studentInfoMap[s.id] || {};
                        return (
                          <option key={s.id} value={s.id}>
                            {info?.name || `Student #${s.id}`}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                )}

                {pdfMode === "range" && (
                  <>
                    <div className="col-md-2">
                      <label className="form-label fw-bold">From Roll</label>
                      <input
                        className="form-control"
                        value={pdfFrom}
                        onChange={(e) => setPdfFrom(e.target.value)}
                        placeholder="e.g. 1"
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label fw-bold">To Roll</label>
                      <input
                        className="form-control"
                        value={pdfTo}
                        onChange={(e) => setPdfTo(e.target.value)}
                        placeholder="e.g. 20"
                      />
                    </div>
                  </>
                )}

                <div className="col-md-2">
                  <button
                    className="btn btn-outline-dark w-100"
                    onClick={downloadPDF}
                    disabled={pdfProgressVisible}
                  >
                    📄 Download PDF
                  </button>
                </div>
              </div>
              <div className="small text-muted mt-2">
                Showing <b>{reportData.length}</b> students (Top 10)
              </div>
            </div>

            {reportData.map((student) => {
              const info = studentInfoMap[student.id] || {};
              const studentPhotoSrc = getStudentPhotoURL(info);

              const subjectsForStudent = getNonDrawingSubjects(student);

              const t1 = term1Id ? getStudentTermOverall(student, term1Id) : null;
              const t2 = term2Id ? getStudentTermOverall(student, term2Id) : null;

              const dobValRaw = info?.Date_Of_Birth || info?.date_of_birth || info?.dob || "";
              const dobVal = formatDOB(dobValRaw);
              const fatherVal = info?.father_name || "-";
              const motherVal = info?.mother_name || "-";

              const effectiveHeaderHtml = getReportCardHeaderHtml();
              const effectiveFooterHtml = getReportCardFooterHtml();
              const effectiveSchoolLogoUrl = getReportCardSchoolLogoUrl();
              const effectiveBoardLogoUrl = getReportCardBoardLogoUrl();

              const cleanHeaderHtml = sanitizeHeaderHtml(effectiveHeaderHtml);

              return (
                <div key={student.id} className="mb-5 p-3 report-card">
                  {cleanHeaderHtml && (
                    <div className="report-header mb-2">
                      <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
                        {effectiveSchoolLogoUrl ? (
                          <img
                            src={effectiveSchoolLogoUrl}
                            alt="School Logo"
                            style={{
                              height: "100px",
                              width: "auto",
                              objectFit: "contain",
                              marginTop: "12px",
                              display: "block",
                              flexShrink: 0,
                            }}
                          />
                        ) : (
                          <div style={{ width: "100px" }} />
                        )}

                        <div
                          className="flex-grow-1 text-center"
                          style={{ fontSize: "15px", lineHeight: 1.32 }}
                          dangerouslySetInnerHTML={{ __html: cleanHeaderHtml }}
                        />

                        <img
                          src={studentPhotoSrc}
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = NO_PHOTO_SVG;
                          }}
                          alt="Student Photo"
                          style={{
                            height: "126px",
                            width: "104px",
                            borderRadius: "12px",
                            objectFit: "cover",
                            border: "2px solid var(--edb-border)",
                            boxShadow: "0 6px 14px color-mix(in srgb, var(--edb-primary-dark) 14%, transparent)",
                            background: "var(--edb-surface)",
                          }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="panel mb-2">
                    <div className="student-info-grid">
                      <div className="student-info-item">
                        <strong>Student Name:</strong> {info?.name || "-"}
                      </div>
                      <div className="student-info-item">
                        <strong>Admission No.:</strong> {info?.admission_number || "-"}
                      </div>
                      <div className="student-info-item">
                        <strong>Class / Section:</strong> {info?.Class?.class_name || "-"} -{" "}
                        {info?.Section?.section_name || "-"}
                      </div>

                      <div className="student-info-item">
                        <strong>Date of Birth:</strong> {dobVal}
                      </div>
                      <div className="student-info-item">
                        <strong>Mother's Name:</strong> {motherVal}
                      </div>
                      <div className="student-info-item">
                        <strong>Father's Name:</strong> {fatherVal}
                      </div>
                    </div>
                  </div>

                  <h5 style={{ fontSize: "18px" }}>Scholastic Areas (Term-wise)</h5>

                  <div className="table-responsive">
                    <table className="table table-bordered text-center small">
                      <thead>
                        <tr>
                          <th
                            rowSpan={2}
                            className="sticky-first-col"
                            style={{
                              background: "linear-gradient(180deg,var(--edb-primary-soft),var(--edb-primary-soft))",
                              color: "var(--edb-primary-dark-text)",
                              textAlign: "left",
                            }}
                          >
                            Subject
                          </th>

                          <th
                            colSpan={term1Components.length + (showTotals ? 2 : 0)}
                            style={{
                              background: "linear-gradient(180deg,var(--edb-primary-soft),var(--edb-primary-soft))",
                              color: "var(--edb-primary-dark-text)",
                            }}
                          >
                            {term1Id ? termLabel(term1Id) : "Term-I"}
                          </th>

                          <th
                            colSpan={term2Components.length + (showTotals ? 2 : 0)}
                            style={{
                              background: "linear-gradient(180deg,var(--edb-primary-soft),var(--edb-primary-soft))",
                              color: "var(--edb-primary-dark-text)",
                            }}
                          >
                            {term2Id ? termLabel(term2Id) : "Term-II"}
                          </th>

                          {showTotals && (
                            <th
                              colSpan={2}
                              style={{
                                background: "linear-gradient(180deg,var(--edb-primary-soft),var(--edb-primary-soft))",
                                color: "var(--edb-primary-dark-text)",
                              }}
                            >
                              Grand Total
                            </th>
                          )}
                        </tr>

                        <tr>
                          {term1Components.map((c) => (
                            <th key={`t1-${c.component_id}`} style={{ backgroundColor: "var(--edb-surface)" }}>
                              {c.label}
                            </th>
                          ))}
                          {showTotals && (
                            <>
                              <th style={{ backgroundColor: "var(--edb-surface)", fontWeight: "bold" }}>Total</th>
                              <th style={{ backgroundColor: "var(--edb-surface)", fontWeight: "bold" }}>Grade</th>
                            </>
                          )}

                          {term2Components.map((c) => (
                            <th key={`t2-${c.component_id}`} style={{ backgroundColor: "var(--edb-surface)" }}>
                              {c.label}
                            </th>
                          ))}
                          {showTotals && (
                            <>
                              <th style={{ backgroundColor: "var(--edb-surface)", fontWeight: "bold" }}>Total</th>
                              <th style={{ backgroundColor: "var(--edb-surface)", fontWeight: "bold" }}>Grade</th>
                            </>
                          )}

                          {showTotals && (
                            <>
                              <th style={{ backgroundColor: "var(--edb-surface)", fontWeight: "bold" }}>Total</th>
                              <th style={{ backgroundColor: "var(--edb-surface)", fontWeight: "bold" }}>Grade</th>
                            </>
                          )}
                        </tr>
                      </thead>

                      <tbody>
                        {subjectsForStudent.map((sub, si) => {
                          const s1 = term1Id ? getSubjectTermStats(student, sub, term1Id) : null;
                          const s2 = term2Id ? getSubjectTermStats(student, sub, term2Id) : null;

                          const allSubj = (student.components || []).filter((c) => c.subject_name === sub);
                          const gMarks = hasAnyMarks(allSubj) ? sumMarksOnly(allSubj) : null;

                          const gW = sumWeightedOnly(allSubj);
                          const gMax = sumMaxWeight(allSubj);
                          const gPct = gMax > 0 ? (gW / gMax) * 100 : null;
                          const gGrade = gPct != null ? gradeFromSchema(gPct, gradeSchema) : pickGrade(allSubj);

                          return (
                            <tr key={si}>
                              <td
                                className="sticky-first-col-td"
                                style={{
                                  backgroundColor: "color-mix(in srgb, var(--edb-primary-soft) 92%, transparent)",
                                  fontWeight: 900,
                                  textAlign: "left",
                                }}
                              >
                                {sub}
                              </td>

                              {term1Components.map((c) => (
                                <td key={`r1-${si}-${c.component_id}`}>
                                  {term1Id
                                    ? getSubjectTermCompDisplay(student, sub, term1Id, c.component_id)
                                    : "-"}
                                </td>
                              ))}
                              {showTotals && (
                                <>
                                  <td style={{ fontWeight: 900 }}>{s1?.marksTotal != null ? s1.marksTotal : "-"}</td>
                                  <td style={{ fontWeight: 900 }}>{s1?.grade || "-"}</td>
                                </>
                              )}

                              {term2Components.map((c) => (
                                <td key={`r2-${si}-${c.component_id}`}>
                                  {term2Id
                                    ? getSubjectTermCompDisplay(student, sub, term2Id, c.component_id)
                                    : "-"}
                                </td>
                              ))}
                              {showTotals && (
                                <>
                                  <td style={{ fontWeight: 900 }}>{s2?.marksTotal != null ? s2.marksTotal : "-"}</td>
                                  <td style={{ fontWeight: 900 }}>{s2?.grade || "-"}</td>
                                </>
                              )}

                              {showTotals && (
                                <>
                                  <td style={{ fontWeight: 900 }}>
                                    <div className="grand-total-small">{gMarks != null ? gMarks : "-"}</div>
                                  </td>
                                  <td style={{ fontWeight: 900 }}>{gGrade || "-"}</td>
                                </>
                              )}
                            </tr>
                          );
                        })}

                        {showTotals && (
                          <>
                            <tr>
                              <td
                                className="sticky-first-col-td"
                                style={{
                                  background: "linear-gradient(180deg,var(--edb-primary-soft),var(--edb-primary-soft))",
                                  fontWeight: 900,
                                  textAlign: "left",
                                  color: "var(--edb-primary-dark-text)",
                                }}
                              >
                                TOTAL
                              </td>

                              {term1Components.map((_, idx) => (
                                <td key={`b1-${idx}`}></td>
                              ))}
                              <td style={{ backgroundColor: "var(--edb-surface)", fontWeight: 900 }}>
                                <div className="grand-total-small">{t1 ? formatNumber(t1.total_weighted) : "-"}</div>
                              </td>
                              <td style={{ backgroundColor: "var(--edb-surface)", fontWeight: 900 }}>
                                {formatPercent(t1?.percent)}
                              </td>

                              {term2Components.map((_, idx) => (
                                <td key={`b2-${idx}`}></td>
                              ))}
                              <td style={{ backgroundColor: "var(--edb-surface)", fontWeight: 900 }}>
                                <div className="grand-total-small">{t2 ? formatNumber(t2.total_weighted) : "-"}</div>
                              </td>
                              <td style={{ backgroundColor: "var(--edb-surface)", fontWeight: 900 }}>
                                {formatPercent(t2?.percent)}
                              </td>

                              <td style={{ backgroundColor: "var(--edb-primary-soft)" }}>
                                <div className="grand-total-small">{formatNumber(student.total_weighted)}</div>
                              </td>

                              <td style={{ backgroundColor: "var(--edb-primary-soft)" }}>
                                {(() => {
                                  const gp = student?.grand_percent_weighted;
                                  const gGrade =
                                    student?.total_grade_weighted ||
                                    (gp != null ? gradeFromSchema(gp, gradeSchema) : null);

                                  return (
                                    <>
                                      {gGrade && gGrade !== "-" ? (
                                        <div className="grand-grade-big">{gGrade}</div>
                                      ) : null}

                                      <div className="grand-percent-highlight">
                                        {gp != null ? `${formatNumber(gp)}%` : "-"}
                                      </div>
                                    </>
                                  );
                                })()}
                              </td>
                            </tr>

                            {hasDisplayRank(student?.rank) && (
                              <tr>
                                <td className="rank-row-label">Rank</td>
                                <td colSpan={getScholasticColumnCount() - 1} className="rank-row-value">
                                  <span className="rank-highlight">{student.rank}</span>
                                </td>
                              </tr>
                            )}
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {gradeFooterText ? (
                    <div className="grade-footer-note">
                      <strong>Grade Scale:</strong> {gradeFooterText}
                    </div>
                  ) : null}

                  <div className="d-flex align-items-center justify-content-between mt-3">
                    <h5 className="mb-0" style={{ fontSize: "18px" }}>Co-Scholastic Area</h5>
                    <div className="section-pill">Co-Scholastic</div>
                  </div>
                  <div className="mt-2">{renderCoScholasticTwoTermsTable(student)}</div>

                  <div className="d-flex align-items-center justify-content-between mt-3">
                    <h5 className="mb-0" style={{ fontSize: "18px" }}>Attendance</h5>
                    <div className="section-pill">Attendance</div>
                  </div>
                  <div className="mt-2">{renderAttendanceTermWise(student.id)}</div>

                  <div className="d-flex align-items-center justify-content-between mt-3">
                    <h5 className="mb-0" style={{ fontSize: "18px" }}>Teacher's Remarks</h5>
                    <div className="section-pill">Remarks</div>
                  </div>
                  <div className="mt-2">{renderTeacherRemarksTermWise(student.id)}</div>

                  {effectiveFooterHtml && (
                    <div
                      className="report-footer mt-3 text-center small"
                      dangerouslySetInnerHTML={{ __html: effectiveFooterHtml }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}

        <Modal show={pdfProgressVisible} centered backdrop="static" keyboard={false}>
          <Modal.Header>
            <Modal.Title>Generating PDF</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-2">{pdfMessage}</div>
            <ProgressBar now={pdfPercent} label={`${pdfPercent}%`} animated />
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                if (abortGenRef.current) abortGenRef.current.abort();
              }}
            >
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default FinalResultSummary;
